import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./options.css";
import "../TradePage/trade_new.css";
import OrderMobileCardShell from "../Order/OrderMobileCardShell";
import "../futures_trade/futures_trade_extras.css";
import useOptionsWebSocket from "./useOptionsWebSocket";
import useOptionsHistory from "./useOptionsHistory";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import AuthService from "../../../api/services/AuthService";
import {
  decNum,
  fmtTs,
  applyChainFilters,
  parseStrikeFilterInput,
  buildDailyExpirySet,
  formatTimeToExpiration,
  formatUnderlyingPair,
  findContractLegInChains,
  findContractRawInChains,
  buildSelectedOptionFromChains,
  parseExpiryFromSymbol,
  getLatestTradePrice,
  resolveUnderlyingIconUrl,
  formatOptionsOrderType,
  formatOptionsOrderStatus,
  formatOptionsOrderIntent,
  isReduceOnlyIntent,
  formatOptionsOpenOrderPrice,
  optionsOpenOrderAvgFill,
  optionsOrderStatusClass,
  computeOptionsPositionAvailable,
  mapOptionsOpenPositionRowView,
  resolveOptionsTradeUserView,
  formatOptionsTradeId,
  formatOptionsTransactionType,
  formatOptionsTransactionDirection,
  formatOptionsTransactionAmount,
  optionsTransactionRowKey,
  formatOptionsOrderHistoryAvgFill,
  formatOptionsOrderHistorySide,
  mapOptionsPositionHistoryRowView,
  decStr,
  formatOptionsAccountUsdt,
  formatOptionsAccountMarginRatioDisplay,
  optionsAccountMarginRatioClass,
  optionsAccountPnlClass,
  calcOptionsInitialMargin,
  calcOptionsAccountImrMmr,
  resolveOptionsAccountUnrealizedPnl,
} from "./optionsDataHelpers";
import {
  newOptionsIdempotencyKey,
  parseOptionsApiError,
  buildDefaultClosePositionInputs,
  validateOptionsClosePosition,
  resolveIncrementSize,
  snapToIncrementInput,
  sanitizeIncrementInput,
  INSUFFICIENT_FUNDS_MSG,
} from "./optionsOrderValidation";
import OptionsOrderbook from "./OptionsOrderbook";
import OptionsPositionSymbolCell from "./OptionsPositionSymbolCell";
import OptionsHistoryEmpty from "./OptionsHistoryEmpty";
import OptionsOrderForm from "./OptionsOrderForm";
import OptionsAccountPanel from "./OptionsAccountPanel";
import OptionsTickerChip from "./OptionsTickerChip";
import OptionsAssetSelect from "./OptionsAssetSelect";
import {
  OptionsActiveTickerPriceSkeleton,
  OptionsChainSkeleton,
  OptionsExpiryBarSkeleton,
  OptionsPairListSkeleton,
  OptionsTickerBarSkeleton,
} from "./OptionsSkeletons";

function CustomFilterDropdown({ options, value, onChange, label }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value) || options[0];

    return (
        <div className="agce-custom-select-container" ref={containerRef}>
            <div 
                className="agce-custom-select-trigger" 
                onClick={() => setIsOpen(!isOpen)}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <span>{selectedOption ? selectedOption.label : label}</span>
                <i className={`ri-arrow-down-s-line ${isOpen ? 'open' : ''}`}></i>
            </div>
            {isOpen && (
                <ul className="agce-custom-select-options" role="listbox">
                    {options.map((o) => (
                        <li 
                            key={o.value || "all"} 
                            className={`agce-custom-select-option ${o.value === value ? 'selected' : ''}`}
                            onClick={() => {
                                onChange(o.value);
                                setIsOpen(false);
                            }}
                            role="option"
                            aria-selected={o.value === value}
                        >
                            {o.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

const SPOT_OPEN_ORDER_KINDS = [
  { id: "all", label: "All" },
  { id: "limit", label: "Limit" },
  { id: "market", label: "Market" },
  { id: "stop", label: "Stop Limit" },
  { id: "stop_market", label: "Stop Market" },
];

function matchesOptionsOpenOrderKind(order, kindTab) {
  if (!kindTab || kindTab === "all") return true;
  const type = String(order.order_type || "").toUpperCase().replace(/-/g, "_");
  const tab = String(kindTab).toUpperCase().replace(/-/g, "_");
  if (tab === "STOP") return type.includes("STOP") && !type.endsWith("MARKET");
  if (tab === "STOP_MARKET") return type === "STOP_MARKET";
  return type === tab;
}

function SpotOpenOrdersFilterRow({
  SPOT_OPEN_ORDER_KINDS,
  openOrderKindTab,
  setOpenOrderKindTab,
  orderType,
  setorderType,
}) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useMemo(
    () => () => {
      const el = scrollerRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const left = el.scrollLeft;
      setCanLeft(left > 2);
      setCanRight(max > 2 && left < max - 2);
    },
    []
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    const t = window.setTimeout(updateArrows, 150);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
    if (ro) ro.observe(el);
    return () => {
      window.clearTimeout(t);
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
      if (ro) ro.disconnect();
    };
  }, [updateArrows, SPOT_OPEN_ORDER_KINDS.length, openOrderKindTab, orderType]);

  const scrollByAmount = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(160, Math.floor(el.clientWidth * 0.65));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <div className="spot_orders_filter_bar">
      <div className="spot_orders_filter_scrollWrap">
        <button
          type="button"
          className={`spot_orders_chipsArrow spot_orders_chipsArrow--left${canLeft ? "" : " is-hidden"}`}
          aria-label="Scroll filters left"
          onClick={() => scrollByAmount(-1)}
        >
          <i className="ri-arrow-left-s-line" aria-hidden="true" />
        </button>

        <div className="spot_orders_filter_chips spot_orders_filter_chips--scroll" ref={scrollerRef}>
          {SPOT_OPEN_ORDER_KINDS.map((k) => (
            <button
              key={k.id}
              type="button"
              className={`spot_orders_chip ${openOrderKindTab === k.id ? "is-active" : ""}`}
              onClick={() => setOpenOrderKindTab(k.id)}
            >
              {k.label}
            </button>
          ))}

          <div className="spot_orders_filter_actions">
            <CustomFilterDropdown
              options={[
                { value: "All", label: "All Sides" },
                { value: "BUY", label: "Buy" },
                { value: "SELL", label: "Sell" }
              ]}
              value={orderType}
              onChange={(val) => setorderType(val)}
              label="All Sides"
            />
            <button
              type="button"
              className="spot_orders_reset"
              onClick={() => {
                setOpenOrderKindTab("all");
                setorderType("All");
              }}
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              Reset
            </button>
          </div>
        </div>

        <button
          type="button"
          className={`spot_orders_chipsArrow spot_orders_chipsArrow--right${canRight ? "" : " is-hidden"}`}
          aria-label="Scroll filters right"
          onClick={() => scrollByAmount(1)}
        >
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function SpotOrdersPrimaryTabsScroll({ positionOrderTab, setPositionOrderTab, positionsCount = 0, openOrdersCount = 0 }) {
  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useMemo(
    () => () => {
      const el = scrollerRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const left = el.scrollLeft;
      setCanLeft(left > 2);
      setCanRight(max > 2 && left < max - 2);
    },
    []
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    const t = window.setTimeout(updateArrows, 150);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateArrows) : null;
    if (ro) ro.observe(el);
    return () => {
      window.clearTimeout(t);
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
      if (ro) ro.disconnect();
    };
  }, [updateArrows, positionOrderTab, positionsCount, openOrdersCount]);

  const scrollByAmount = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(120, Math.floor(el.clientWidth * 0.6));
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  };

  return (
    <div className="spot_orders_primary_tabs_scrollWrap">
      <button
        type="button"
        className={`spot_orders_chipsArrow spot_orders_chipsArrow--left${canLeft ? "" : " is-hidden"}`}
        aria-label="Scroll tabs left"
        onClick={() => scrollByAmount(-1)}
      >
        <i className="ri-arrow-left-s-line" aria-hidden="true" />
      </button>

      <ul className="position_list spot_orders_primary_tabs spot_orders_primary_tabs--scroller" ref={scrollerRef}>
        <li className={`nav-item ${positionOrderTab === "positions" ? "active" : ""}`} role="presentation">
          <button type="button" onClick={() => setPositionOrderTab("positions")}>
            {`Positions(${positionsCount})`}
          </button>
        </li>
        <li className={`nav-item ${positionOrderTab === "positionHistory" ? "active" : ""}`} role="presentation">
          <button type="button" onClick={() => setPositionOrderTab("positionHistory")}>
            Position History
          </button>
        </li>
        <li className={`nav-item ${positionOrderTab === "openOrders" ? "active" : ""}`} role="presentation">
          <button type="button" onClick={() => setPositionOrderTab("openOrders")}>
            {`Open Orders(${openOrdersCount})`}
          </button>
        </li>
        <li className={`nav-item ${positionOrderTab === "orderHistory" ? "active" : ""}`} role="presentation">
          <button type="button" onClick={() => setPositionOrderTab("orderHistory")}>
            Order History
          </button>
        </li>
        <li className={`nav-item ${positionOrderTab === "tradeHistory" ? "active" : ""}`} role="presentation">
          <button type="button" onClick={() => setPositionOrderTab("tradeHistory")}>
            Trade History
          </button>
        </li>
        <li className={`nav-item ${positionOrderTab === "transactionHistory" ? "active" : ""}`} role="presentation">
          <button type="button" onClick={() => setPositionOrderTab("transactionHistory")}>
            Transaction History
          </button>
        </li>
      </ul>

      <button
        type="button"
        className={`spot_orders_chipsArrow spot_orders_chipsArrow--right${canRight ? "" : " is-hidden"}`}
        aria-label="Scroll tabs right"
        onClick={() => scrollByAmount(1)}
      >
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>
    </div>
  );
}

const OPTIONS_EXPIRY_ALL = "ALL";

// Helper to format numbers nicely
const fmt = (num, dec = 2) => Number(num).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtOptPrice = (v, dec = 0) => {
  const n = decNum(v);
  if (!Number.isFinite(n) || n <= 0) return "--";
  return fmt(n, dec);
};

const fmtIvPct = (v) => {
  const n = decNum(v);
  if (!Number.isFinite(n) || n <= 0) return "--";
  return `${fmt(n, 2)}%`;
};

const OptionsPage = () => {
  const { pairs } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = typeof localStorage !== "undefined"
    ? (localStorage.getItem("token") || sessionStorage.getItem("token"))
    : null;
  const userId = typeof localStorage !== "undefined"
    ? (localStorage.getItem("userId") || sessionStorage.getItem("userId"))
    : null;

  const initialAsset = pairs ? pairs.split("_")[0] : null;
  const [selectedAsset, setSelectedAsset] = useState(initialAsset);
  const [selectedDate, setSelectedDate] = useState(() => {
    try {
      const c = new URLSearchParams(window.location.search).get("contract");
      return c ? parseExpiryFromSymbol(c) : null;
    } catch {
      return null;
    }
  });
  const contractDismissedRef = useRef(false);
  const [oddSize, setOddSize] = useState(true);
  const [strikeMinInput, setStrikeMinInput] = useState("");
  const [strikeMaxInput, setStrikeMaxInput] = useState("");
  const [tteTick, setTteTick] = useState(Date.now());
  const [search, setSearch] = useState("");
  const [coinFilter, setCoinFilter] = useState("USDT");
  const [showMenuPopup, setShowMenuPopup] = useState(false);
  const [showAccountModeModal, setShowAccountModeModal] = useState(false);
  const [showPortfolioMore, setShowPortfolioMore] = useState(false);
  const [showClassicMore, setShowClassicMore] = useState(false);
  const [showSingleMore, setShowSingleMore] = useState(false);
  const [showMultiMore, setShowMultiMore] = useState(false);
  const [activeAccountMode, setActiveAccountMode] = useState("single");

  // Tickers scroll logic
  const tickersScrollRef = useRef(null);
  const [canScrollTickersLeft, setCanScrollTickersLeft] = useState(false);
  const [canScrollTickersRight, setCanScrollTickersRight] = useState(false);

  const updateTickersArrows = () => {
    if (!tickersScrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = tickersScrollRef.current;
    setCanScrollTickersLeft(scrollLeft > 2);
    setCanScrollTickersRight(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 2);
  };

  useEffect(() => {
    updateTickersArrows();
    window.addEventListener("resize", updateTickersArrows);
    return () => window.removeEventListener("resize", updateTickersArrows);
  }, [selectedAsset]); // Re-check when asset changes

  // Dates scroll logic
  const datesScrollRef = useRef(null);
  const [canScrollDatesLeft, setCanScrollDatesLeft] = useState(false);
  const [canScrollDatesRight, setCanScrollDatesRight] = useState(false);

  const updateDatesArrows = () => {
    if (!datesScrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = datesScrollRef.current;
    setCanScrollDatesLeft(scrollLeft > 2);
    setCanScrollDatesRight(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 2);
  };

  useEffect(() => {
    const t = setTimeout(updateDatesArrows, 100);
    window.addEventListener("resize", updateDatesArrows);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", updateDatesArrows);
    };
  }, [selectedDate]);

  const scrollDates = (dir) => {
    if (datesScrollRef.current) {
      datesScrollRef.current.scrollBy({ left: dir * 150, behavior: "smooth" });
    }
  };

  // Filters scroll logic
  const filtersScrollRef = useRef(null);
  const [canScrollFiltersLeft, setCanScrollFiltersLeft] = useState(false);
  const [canScrollFiltersRight, setCanScrollFiltersRight] = useState(false);

  const updateFiltersArrows = () => {
    if (!filtersScrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = filtersScrollRef.current;
    setCanScrollFiltersLeft(scrollLeft > 2);
    setCanScrollFiltersRight(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 2);
  };

  useEffect(() => {
    const t = setTimeout(updateFiltersArrows, 100);
    window.addEventListener("resize", updateFiltersArrows);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", updateFiltersArrows);
    };
  }, []);

  const scrollFilters = (dir) => {
    if (filtersScrollRef.current) {
      filtersScrollRef.current.scrollBy({ left: dir * 150, behavior: "smooth" });
    }
  };

  // Disable body scroll when Switch Account Mode modal is open
  useEffect(() => {
    if (showAccountModeModal) {
      document.body.classList.add("modal-open");
      document.documentElement.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
      document.documentElement.classList.remove("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.documentElement.classList.remove("modal-open");
    };
  }, [showAccountModeModal]);

  const scrollTickers = (dir) => {
    if (tickersScrollRef.current) {
      tickersScrollRef.current.scrollBy({ left: dir * 200, behavior: "smooth" });
    }
  };
  const [orderSide, setOrderSide] = useState("buy");
  const [obView, setObView] = useState("both");

  // Mobile trade center tab state
  const [mobileTradeTab, setMobileTradeTab] = useState("buy");

  const [cols, setCols] = useState({
    last: true,
    vega: true,
    theta: true,
    gamma: true,
  });
  const [showColMenu, setShowColMenu] = useState(false);

  // Tabs state for TradeHistorySection
  const [positionOrderTab, setPositionOrderTab] = useState("positions");
  const [openOrderKindTab, setOpenOrderKindTab] = useState("all");
  const [orderType, setorderType] = useState("All");
  const [selectedOption, setSelectedOption] = useState(null);

  const selectedContractSymbol = selectedOption?.symbol ?? null;

  const optionsContractPath = useCallback((asset, contractSymbol) => {
    const base = `/options/${asset}_USDT`;
    if (!contractSymbol) return base;
    return `${base}?contract=${encodeURIComponent(contractSymbol)}`;
  }, []);

  const selectContract = useCallback((opt, { dismissed = false } = {}) => {
    contractDismissedRef.current = dismissed;
    setSelectedOption(opt);
    if (opt?.date) {
      setSelectedDate((prev) => (prev === OPTIONS_EXPIRY_ALL ? prev : opt.date));
    }
    const asset = selectedAsset || (pairs ? pairs.split("_")[0] : null);
    if (!asset) return;
    navigate(optionsContractPath(asset, opt?.symbol || null), { replace: true });
  }, [navigate, optionsContractPath, pairs, selectedAsset]);

  const {
    underlyings: wsUnderlyings,
    expiryDates,
    indexPrice,
    marketOverview,
    allChains,
    accountUpdate,
    orderbook,
    recentTrades,
    userOrders,
    userPositions,
    isUserOrdersLoading,
    isUserPositionsLoading,
    isMarketLoading,
    isContractsLoading,
    isAccountLoading,
    refreshLiveTradeChannels,
  } = useOptionsWebSocket({
    token,
    selectedAsset: selectedAsset || "",
    selectedExpiry: selectedDate === OPTIONS_EXPIRY_ALL ? OPTIONS_EXPIRY_ALL : selectedDate,
    selectedContractSymbol,
    enabled: true,
  });

  const selectedUnderlyingMeta = useMemo(
    () => wsUnderlyings.find((u) => u.symbol === selectedAsset) ?? null,
    [wsUnderlyings, selectedAsset]
  );

  const expiryCountForUnderlying = useCallback((underlyingKey) => {
    const list = marketOverview?.expiry_dates?.[underlyingKey];
    return Array.isArray(list) ? list.length : 0;
  }, [marketOverview?.expiry_dates]);

  const {
    positionHistory,
    orderHistory,
    tradeHistory,
    transactionHistory,
    loading: historyLoading,
  } = useOptionsHistory({
    token,
    activeTab: positionOrderTab,
    underlying: selectedUnderlyingMeta?.underlying || (selectedAsset ? `${selectedAsset}USDT` : undefined),
    symbol: selectedContractSymbol || undefined,
  });

  const openOrders = userOrders;
  const openPositions = userPositions;
  const openPositionsLoading = isUserPositionsLoading;
  const openOrdersLoading = isUserOrdersLoading;

  const hasMarketData = wsUnderlyings.length > 0;

  const showMarketSkeleton = isMarketLoading;
  const showContractsSkeleton = isContractsLoading;
  const showAccountSkeleton = Boolean(token) && isAccountLoading;

  useEffect(() => {
    updateTickersArrows();
  }, [wsUnderlyings.length, selectedAsset]);

  useEffect(() => {
    setSelectedDate((prev) => {
      if (prev === OPTIONS_EXPIRY_ALL) return prev;
      // Keep URL/init expiry while overview is loading or once it appears in the list.
      if (prev && (expiryDates.length === 0 || expiryDates.includes(prev))) return prev;
      return expiryDates.length ? expiryDates[0] : null;
    });
  }, [expiryDates, selectedAsset]);

  useEffect(() => {
    const t = setTimeout(updateDatesArrows, 100);
    updateDatesArrows();
    return () => clearTimeout(t);
  }, [expiryDates.length, selectedDate]);

  useEffect(() => {
    if (wsUnderlyings.length === 0) return;
    const symbols = wsUnderlyings.map((u) => u.symbol);
    if (!selectedAsset || !symbols.includes(selectedAsset)) {
      const next = symbols[0];
      contractDismissedRef.current = false;
      setSelectedAsset(next);
      setSelectedDate(null);
      setSelectedOption(null);
      navigate(`/options/${next}_USDT`, { replace: true });
    }
  }, [wsUnderlyings, selectedAsset, navigate]);

  const spotPrice = indexPrice > 0 ? indexPrice : null;

  const strikeMin = parseStrikeFilterInput(strikeMinInput);
  const strikeMax = parseStrikeFilterInput(strikeMaxInput);

  const displayChains = useMemo(
    () => applyChainFilters(allChains, { oddSize, strikeMin, strikeMax }),
    [allChains, oddSize, strikeMin, strikeMax]
  );

  const chainEmptyMessage = allChains.length === 0
    ? "No active contracts for this expiry"
    : "No contracts match your filters";

  const handleFilterReset = () => {
    setOddSize(true);
    setStrikeMinInput("");
    setStrikeMaxInput("");
  };

  // Restore contract from ?contract= only (once). Never fight manual expiry clicks.
  useEffect(() => {
    if (!selectedAsset) return;
    if (contractDismissedRef.current) return;

    const urlSym = new URLSearchParams(location.search).get("contract");

    if (!urlSym) {
      setSelectedOption((prev) => (prev ? null : prev));
      return;
    }

    // Already restored this contract — allow free date browsing.
    if (selectedOption?.symbol === urlSym) return;

    const expiry = parseExpiryFromSymbol(urlSym);
    if (
      expiry &&
      selectedDate !== OPTIONS_EXPIRY_ALL &&
      selectedDate !== expiry
    ) {
      setSelectedDate(expiry);
      return;
    }

    if (isContractsLoading) return;

    const fromUrl = buildSelectedOptionFromChains(allChains, urlSym);
    if (fromUrl) {
      setSelectedOption((prev) => (prev?.symbol === fromUrl.symbol ? prev : fromUrl));
      return;
    }

    // Initial restore only: load all expiries to find the URL contract.
    if (selectedDate !== OPTIONS_EXPIRY_ALL) {
      setSelectedDate(OPTIONS_EXPIRY_ALL);
    }
  }, [
    allChains,
    isContractsLoading,
    selectedAsset,
    selectedDate,
    selectedOption?.symbol,
    location.search,
  ]);

  const handleSelectExpiry = useCallback((d) => {
    setSelectedDate(d);
    const urlSym = new URLSearchParams(location.search).get("contract");
    // Leaving the selected contract's expiry — clear selection + URL so restore won't snap back.
    if (!urlSym && !selectedOption) return;
    const contractExpiry = selectedOption?.date || (urlSym ? parseExpiryFromSymbol(urlSym) : null);
    if (d === OPTIONS_EXPIRY_ALL || !contractExpiry || d === contractExpiry) return;
    setSelectedOption(null);
    if (selectedAsset) {
      navigate(`/options/${selectedAsset}_USDT`, { replace: true });
    }
  }, [location.search, navigate, selectedAsset, selectedOption]);

  const acctNum = useCallback((field) => decNum(accountUpdate?.[field]), [accountUpdate]);
  const fmtAcct = useCallback(
    (field, decimals = 2) => (token && accountUpdate ? fmt(acctNum(field), decimals) : "—"),
    [token, accountUpdate, acctNum]
  );

  const optionsAccountPanel = useMemo(() => {
    const dash = "—";
    if (!token) {
      return {
        account: dash,
        marginRatio: dash,
        marginRatioClass: "",
        maintenanceMargin: dash,
        adjustedEquity: dash,
        totalEquity: dash,
        marketValue: dash,
        marginBalance: dash,
        availableMargin: dash,
        initialMargin: dash,
        lockedInOrders: dash,
        unrealizedPnl: dash,
        unrealizedPnlClass: "",
        imr: dash,
        mmr: dash,
        imrClass: "",
        mmrClass: "",
        imrProgress: 0,
        mmrProgress: 0,
        imrPct: 0,
        mmrPct: 0,
      };
    }
    if (!accountUpdate) {
      return null;
    }
    const initialMargin = calcOptionsInitialMargin(accountUpdate, openPositions);
    const imrMmr = calcOptionsAccountImrMmr(accountUpdate, openPositions);
    const positionsReady = !isUserPositionsLoading;
    const unrealizedPnl = resolveOptionsAccountUnrealizedPnl(
      accountUpdate,
      openPositions,
      positionsReady
    );
    return {
      account: formatOptionsAccountUsdt(accountUpdate.available_balance),
      marginRatio: formatOptionsAccountMarginRatioDisplay(accountUpdate, openPositions),
      marginRatioClass: optionsAccountMarginRatioClass(accountUpdate, openPositions),
      maintenanceMargin: formatOptionsAccountUsdt(accountUpdate.maintenance_margin),
      adjustedEquity: "—",
      totalEquity: formatOptionsAccountUsdt(accountUpdate.total_equity),
      marketValue: formatOptionsAccountUsdt(accountUpdate.market_value),
      marginBalance: formatOptionsAccountUsdt(accountUpdate.margin_balance),
      availableMargin: formatOptionsAccountUsdt(accountUpdate.available_margin),
      initialMargin: formatOptionsAccountUsdt(initialMargin),
      lockedInOrders: formatOptionsAccountUsdt(accountUpdate.in_order),
      unrealizedPnl: formatOptionsAccountUsdt(unrealizedPnl),
      unrealizedPnlClass: optionsAccountPnlClass(unrealizedPnl),
      imr: imrMmr.imr,
      mmr: imrMmr.mmr,
      imrClass: imrMmr.imrClass,
      mmrClass: imrMmr.mmrClass,
      imrProgress: imrMmr.imrProgress,
      mmrProgress: imrMmr.mmrProgress,
      imrPct: imrMmr.imrPct,
      mmrPct: imrMmr.mmrPct,
    };
  }, [token, accountUpdate, openPositions, isUserPositionsLoading]);

  const obAskRows = useMemo(() => [...(orderbook?.asks || [])].reverse().slice(0, 12), [orderbook]);
  const obBidRows = useMemo(() => (orderbook?.bids || []).slice(0, 12), [orderbook]);

  const selectedContractLeg = useMemo(
    () => findContractLegInChains(allChains, selectedContractSymbol) ?? selectedOption?.data ?? null,
    [allChains, selectedContractSymbol, selectedOption?.data]
  );

  const selectedContractRaw = useMemo(
    () =>
      findContractRawInChains(allChains, selectedContractSymbol)
      ?? selectedOption?.contractRaw
      ?? null,
    [allChains, selectedContractSymbol, selectedOption?.contractRaw]
  );

  const activeContractTickSize = selectedContractRaw?.tick_size ?? selectedOption?.contractRaw?.tick_size;
  const activeContractStepSize = selectedContractRaw?.step_size ?? selectedOption?.contractRaw?.step_size;

  const symbolPosition = useMemo(() => {
    if (!selectedContractSymbol) return null;
    return openPositions.find((p) => p.symbol === selectedContractSymbol) ?? null;
  }, [openPositions, selectedContractSymbol]);

  const symbolOpenOrders = useMemo(() => {
    if (!selectedContractSymbol) return [];
    return openOrders.filter((o) => o.symbol === selectedContractSymbol);
  }, [openOrders, selectedContractSymbol]);

  const filteredOpenOrders = useMemo(
    () => openOrders
      .filter((item) => orderType === "All" || String(item.side || "").toUpperCase() === orderType)
      .filter((item) => matchesOptionsOpenOrderKind(item, openOrderKindTab)),
    [openOrders, orderType, openOrderKindTab]
  );

  const [actionCooldownKeys, setActionCooldownKeys] = useState(() => new Set());
  const cooldownTimersRef = useRef({});
  const ACTION_COOLDOWN_MS = 5000;

  const isActionCoolingDown = useCallback(
    (key) => actionCooldownKeys.has(key),
    [actionCooldownKeys]
  );

  const armActionCooldown = useCallback((key) => {
    if (!key) return;
    setActionCooldownKeys((prev) => new Set(prev).add(key));
    if (cooldownTimersRef.current[key]) {
      clearTimeout(cooldownTimersRef.current[key]);
    }
    cooldownTimersRef.current[key] = window.setTimeout(() => {
      setActionCooldownKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      delete cooldownTimersRef.current[key];
    }, ACTION_COOLDOWN_MS);
  }, []);

  useEffect(() => () => {
    Object.values(cooldownTimersRef.current).forEach(clearTimeout);
    cooldownTimersRef.current = {};
  }, []);

  const handleCancelOptionOrder = useCallback(async (orderId) => {
    const key = `cancel:${orderId}`;
    if (!orderId || !token || isActionCoolingDown(key)) return;
    armActionCooldown(key);
    try {
      const res = await AuthService.cancelOptionOrder(orderId);
      if (res?.success) {
        alertSuccessMessage("Cancel request submitted.");
        refreshLiveTradeChannels();
      } else {
        alertErrorMessage(parseOptionsApiError(res));
      }
    } catch {
      alertErrorMessage("Failed to cancel order.");
    }
  }, [token, armActionCooldown, isActionCoolingDown, refreshLiveTradeChannels]);

  const [closeInputs, setCloseInputs] = useState({});
  const [closingPosSymbol, setClosingPosSymbol] = useState(null);
  const closePrefilledRef = useRef(new Set());

  useEffect(() => {
    const activeSymbols = new Set(openPositions.map((p) => p.symbol).filter(Boolean));
    for (const sym of closePrefilledRef.current) {
      if (!activeSymbols.has(sym)) closePrefilledRef.current.delete(sym);
    }

    setCloseInputs((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const pos of openPositions) {
        const sym = pos.symbol;
        if (!sym || closePrefilledRef.current.has(sym)) continue;
        const contractRaw = findContractRawInChains(allChains, sym);
        if (!contractRaw) continue;
        const defaults = buildDefaultClosePositionInputs(pos, contractRaw, openOrders);
        next[sym] = { price: defaults.price, qty: defaults.qty };
        closePrefilledRef.current.add(sym);
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [openPositions, allChains, openOrders]);

  const setCloseInput = useCallback((symbol, field, value) => {
    setCloseInputs((prev) => ({
      ...prev,
      [symbol]: { ...(prev[symbol] || {}), [field]: value },
    }));
  }, []);

  const snapCloseInputField = useCallback(
    (pos, field) => {
      const sym = pos?.symbol;
      if (!sym) return;
      const contractRaw = findContractRawInChains(allChains, sym);
      const tickSize = resolveIncrementSize(null, contractRaw, "tick_size");
      const stepSize = resolveIncrementSize(null, contractRaw, "step_size");
      setCloseInputs((prev) => {
        const current = prev[sym]?.[field] ?? "";
        const snapped =
          field === "price"
            ? snapToIncrementInput(current, tickSize)
            : snapToIncrementInput(current, stepSize);
        if (snapped === current) return prev;
        return { ...prev, [sym]: { ...(prev[sym] || {}), [field]: snapped } };
      });
    },
    [allChains]
  );

  const handleOptionsLimitClose = useCallback(async (pos) => {
    const key = `close:${pos.symbol}`;
    if (!token || !pos?.symbol || isActionCoolingDown(key)) return;

    const contractRaw = findContractRawInChains(allChains, pos.symbol);
    const tickSize = resolveIncrementSize(null, contractRaw, "tick_size");
    const stepSize = resolveIncrementSize(null, contractRaw, "step_size");
    const inp = closeInputs[pos.symbol] || {};
    const snappedPrice = snapToIncrementInput(String(inp.price ?? ""), tickSize);
    const snappedQty = snapToIncrementInput(String(inp.qty ?? ""), stepSize);

    if (snappedPrice !== inp.price || snappedQty !== inp.qty) {
      setCloseInputs((prev) => ({
        ...prev,
        [pos.symbol]: { price: snappedPrice, qty: snappedQty },
      }));
    }

    const avail = computeOptionsPositionAvailable(pos, openOrders);
    if (avail <= 0) {
      alertErrorMessage("No available size to close.");
      return;
    }

    const avblUsdt = decNum(accountUpdate?.available_balance);
    const validation = validateOptionsClosePosition({
      position: pos,
      price: snappedPrice,
      quantity: snappedQty,
      contractRaw,
      openOrders,
      availableBalance: avblUsdt,
      indexPrice,
      feeRates: marketOverview?.fee_rates,
    });

    if (!validation.ok) {
      if (validation.message !== INSUFFICIENT_FUNDS_MSG) {
        alertErrorMessage(validation.message);
      }
      return;
    }

    armActionCooldown(key);
    setClosingPosSymbol(pos.symbol);
    try {
      const res = await AuthService.placeOptionOrder(validation.payload, newOptionsIdempotencyKey());
      if (res?.success) {
        alertSuccessMessage("Close order submitted.");
        refreshLiveTradeChannels();
      } else {
        alertErrorMessage(parseOptionsApiError(res));
      }
    } catch {
      alertErrorMessage("Failed to close position.");
    } finally {
      setClosingPosSymbol(null);
    }
  }, [
    token,
    closeInputs,
    openOrders,
    allChains,
    accountUpdate,
    indexPrice,
    marketOverview?.fee_rates,
    isActionCoolingDown,
    armActionCooldown,
    refreshLiveTradeChannels,
  ]);

  const renderOptionsClosePositionActions = useCallback((pos) => {
    const sym = pos.symbol;
    const closeCooldownKey = `close:${sym}`;
    const contractRaw = findContractRawInChains(allChains, sym);
    const tickSize = resolveIncrementSize(null, contractRaw, "tick_size");
    const stepSize = resolveIncrementSize(null, contractRaw, "step_size");
    const inp = closeInputs[sym] || { price: "", qty: "" };
    const isClosing = closingPosSymbol === sym;
    const closeDisabled = isClosing || isActionCoolingDown(closeCooldownKey);
    const avail = computeOptionsPositionAvailable(pos, openOrders);
    const submitClose = () => {
      if (!closeDisabled && avail > 0) handleOptionsLimitClose(pos);
    };
    return (
      <div className="agcex-opt-close-pos">
        <button
          type="button"
          className="agcex-opt-close-pos__label"
          disabled={closeDisabled || avail <= 0}
          onClick={submitClose}
        >
          {isClosing ? "…" : "Close"}
        </button>
        <input
          type="text"
          className="agcex-opt-close-pos__input"
          value={inp.price}
          disabled={closeDisabled}
          onChange={(e) =>
            setCloseInput(sym, "price", sanitizeIncrementInput(e.target.value, tickSize))
          }
          onBlur={() => snapCloseInputField(pos, "price")}
          onKeyDown={(e) => e.key === "Enter" && submitClose()}
          aria-label="Close limit price"
        />
        <input
          type="text"
          className="agcex-opt-close-pos__input"
          value={inp.qty}
          disabled={closeDisabled}
          onChange={(e) =>
            setCloseInput(sym, "qty", sanitizeIncrementInput(e.target.value, stepSize))
          }
          onBlur={() => snapCloseInputField(pos, "qty")}
          onKeyDown={(e) => e.key === "Enter" && submitClose()}
          aria-label="Close quantity"
        />
      </div>
    );
  }, [
    closeInputs,
    closingPosSymbol,
    openOrders,
    allChains,
    isActionCoolingDown,
    handleOptionsLimitClose,
    setCloseInput,
    snapCloseInputField,
  ]);

  const obLastPrice = useMemo(() => {
    const fromTrade = getLatestTradePrice(recentTrades);
    if (fromTrade != null) return fromTrade;
    const legLast = selectedContractLeg?.last ?? selectedContractLeg?.markIv;
    return legLast > 0 ? legLast : null;
  }, [recentTrades, selectedContractLeg]);

  const obIvPct = useMemo(() => {
    const iv = selectedContractLeg?.markIvPct;
    return iv > 0 ? iv : null;
  }, [selectedContractLeg]);

  useEffect(() => {
    const id = window.setInterval(() => setTteTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const dailyExpirySet = useMemo(() => buildDailyExpirySet(expiryDates), [expiryDates]);
  const formatTte = useCallback(
    (expiryDate) => formatTimeToExpiration(expiryDate, dailyExpirySet, tteTick),
    [dailyExpirySet, tteTick]
  );

  const callVol24h = useMemo(() => {
    let sum = 0;
    for (const chain of displayChains) {
      for (const row of chain.data) {
        if (row.callRaw) sum += decNum(row.callRaw.volume);
      }
    }
    return sum;
  }, [displayChains]);

  const putVol24h = useMemo(() => {
    let sum = 0;
    for (const chain of displayChains) {
      for (const row of chain.data) {
        if (row.putRaw) sum += decNum(row.putRaw.volume);
      }
    }
    return sum;
  }, [displayChains]);

  const callRecentTrades = useMemo(
    () => recentTrades.filter((t) => String(t.symbol || "").toUpperCase().endsWith("-C")),
    [recentTrades]
  );
  const putRecentTrades = useMemo(
    () => recentTrades.filter((t) => String(t.symbol || "").toUpperCase().endsWith("-P")),
    [recentTrades]
  );

  const mapRecentTradeRow = (t) => {
    const side = String(t.side || "").toUpperCase();
    return {
      id: t.trade_id || t._id,
      side: side === "BUY" ? "Buy" : side === "SELL" ? "Sell" : side,
      symbol: t.symbol || "—",
      price: fmt(decNum(t.price), 2),
      size: decNum(t.quantity),
      time: fmtTs(t.timestamp),
    };
  };

  // Handle scroll lock on mobile when sidebar is open
  useEffect(() => {
    if (selectedOption) {
      document.body.classList.add("has-sidebar-open");
      document.documentElement.classList.add("has-sidebar-open");
    } else {
      document.body.classList.remove("has-sidebar-open");
      document.documentElement.classList.remove("has-sidebar-open");
    }
    return () => {
      document.body.classList.remove("has-sidebar-open");
      document.documentElement.classList.remove("has-sidebar-open");
    };
  }, [selectedOption]);

  const activeCols = selectedOption ? {
    last: false,
    vega: false,
    theta: false,
    gamma: false,
  } : cols;

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);
  const [isCoinSelectOpen, setIsCoinSelectOpen] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [fromWalletType, setFromWalletType] = useState("isolated");
  const [toWalletType, setToWalletType] = useState("spot");

  const [tradeCenterChartTab, setTradeCenterChartTab] = useState("pnl");

  const [showMarkSlider, setShowMarkSlider] = useState(false);
  const [markSliderValue, setMarkSliderValue] = useState(0);

  // Refs for scroll syncing
  const callsHeaderRef = useRef(null);
  const callsBodyRef = useRef(null);
  const putsHeaderRef = useRef(null);
  const putsBodyRef = useRef(null);

  const handleCallsScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    if (callsHeaderRef.current) callsHeaderRef.current.scrollLeft = scrollLeft;
    if (putsBodyRef.current && putsBodyRef.current.scrollLeft !== scrollLeft) {
      putsBodyRef.current.scrollLeft = scrollLeft;
      if (putsHeaderRef.current) putsHeaderRef.current.scrollLeft = scrollLeft;
    }
  };

  const handlePutsScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    if (putsHeaderRef.current) putsHeaderRef.current.scrollLeft = scrollLeft;
    if (callsBodyRef.current && callsBodyRef.current.scrollLeft !== scrollLeft) {
      callsBodyRef.current.scrollLeft = scrollLeft;
      if (callsHeaderRef.current) callsHeaderRef.current.scrollLeft = scrollLeft;
    }
  };

  useEffect(() => {
    if (pairs) {
      const asset = pairs.split("_")[0];
      setSelectedAsset(asset);
      contractDismissedRef.current = false;
    }
  }, [pairs]);

  const handleAssetClick = (symbol) => {
    contractDismissedRef.current = false;
    setSelectedAsset(symbol);
    setSelectedDate(null);
    setSelectedOption(null);
    navigate(`/options/${symbol}_USDT`);
  };

  const renderSpotLists = () => (
    <div className="spotLists">
      {/* Search */}
      <div className="spot-list-search">
        <div className="ivu-input">
          <i className="ri-search-2-line"></i>
          <input
            autoComplete="off"
            spellCheck="false"
            type="search"
            placeholder="Search"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </div>
      </div>

      <ul className="favorites_list_tabs">
        <li>
          <button
            className={coinFilter === 'USDT' ? 'active' : ''}
            onClick={() => setCoinFilter('USDT')}
          >
            USDT
          </button>
        </li>
        <li>
          <button
            className={coinFilter === 'USDC' ? 'active' : ''}
            onClick={() => setCoinFilter('USDC')}
          >
            USDC
          </button>
        </li>
      </ul>

      {/* Table */}
      <div className="price_card table-responsive">
        <table className="table table-sm table-borderless mb-0 orderbook-table">
          <thead>
            <tr>
              <th>Pair</th>
              <th className="text-end">Price</th>
              <th className="text-end">Change</th>
            </tr>
          </thead>
          <tbody className="price_card_body">
            {showMarketSkeleton ? (
              <OptionsPairListSkeleton rows={6} />
            ) : !hasMarketData ? (
              <tr>
                <td colSpan={3} className="text-center text-muted py-3">
                  No underlyings available
                </td>
              </tr>
            ) : wsUnderlyings.filter(u => u.symbol.toLowerCase().includes(search.toLowerCase())).map((u) => (
              <tr
                key={u.symbol}
                className={selectedAsset === u.symbol ? "active" : ""}
                style={{ cursor: "pointer" }}
                onClick={() => {
                  handleAssetClick(u.symbol);
                  setShowMenuPopup(false);
                }}
              >
                {/* Pair */}
                <td>
                  <div className="d-flex align-items-center gap-1">
                    <img
                      src={resolveUnderlyingIconUrl(u.iconPath)}
                      alt=""
                      className="img-fluid me-1 round_img"
                      onError={(e) => { e.target.src = '/images/coin_placeholder.png'; }}
                    />
                    <div className="d-flex flex-column pair-color">
                      {formatUnderlyingPair(u.underlying)}
                      <span className="tokensubcnt">{u.symbol}</span>
                    </div>
                  </div>
                </td>

                {/* Price */}
                <td className="text-end">
                  <div className="d-flex flex-column pair-color">
                    <span>{u.price > 0 ? fmt(u.price, 2) : "—"}</span>
                    <span className="tokensubcnt">{u.price > 0 ? `$${fmt(u.price, 2)}` : "—"}</span>
                  </div>
                </td>

                {/* Change */}
                <td className="text-end">
                  <div className="d-flex justify-content-end align-items-center gap-2">
                    <div className="d-flex flex-column pair-color text-end">
                      <span className="text-muted">—</span>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className={`agcex-options-wrapper ${showAccountModeModal ? "modal-open" : ""}`}>
      <Helmet>
        <title>Options Trading | AGCX</title>
      </Helmet>

      {/* Top Tickers Bar */}
      <div className="options-tickers-bar" style={{ position: "relative" }}>

        <div className="hdr_lft_list d-flex align-items-center gap-2">

          {/* <i className="ri-menu-line" style={{ fontSize: "18px", color: "#848e9c", cursor: "pointer" }} onClick={() => setShowMenuPopup(prev => !prev)}></i> */}

          {/* Trade Favourites Desktop Dropdown */}
          <div className="trade-wrapper spot desktop-favourites-dropdown" style={{ padding: 0, position: "static" }}>
            <div className={`col-12 col-lg-12 col-xl-2 col-xxl-2 trade_favourites_lft ${showMenuPopup ? "is-open" : "is-collapsed"}`}>
              {renderSpotLists()}
            </div>
          </div>

          {/* Mobile Favourites Popup (styled as overlay/drawer) */}
          {showMenuPopup && (
            <div className="mobile-favourites-popup-overlay-options" onClick={() => setShowMenuPopup(false)}>
              <div className="mobile-favourites-popup-options" onClick={(e) => e.stopPropagation()}>
                <div className="mobile-favourites-popup-header-options">
                  <h4>Select Asset</h4>
                  <button className="mobile-favourites-close-btn-options" onClick={() => setShowMenuPopup(false)}>×</button>
                </div>
                <div className="mobile-favourites-popup-content-options">
                  {renderSpotLists()}
                </div>
              </div>
            </div>
          )}

          {/* Scrollable Area for Tickers */}
          {canScrollTickersLeft && (
            <button className="options-scroll-arrow left" onClick={() => scrollTickers(-1)}>
              <i className="ri-arrow-left-s-line"></i>
            </button>
          )}
          <div
            className="options-tickers-scroll"
            ref={tickersScrollRef}
            onScroll={updateTickersArrows}
          >
            {/* Active Asset Block */}
            <div className="options-ticker-active-block">
              {showMarketSkeleton || !selectedUnderlyingMeta ? (
                <OptionsActiveTickerPriceSkeleton />
              ) : (
                <OptionsTickerChip
                  underlying={selectedUnderlyingMeta.underlying}
                  symbol={selectedUnderlyingMeta.symbol}
                  iconPath={resolveUnderlyingIconUrl(selectedUnderlyingMeta.iconPath)}
                  price={spotPrice}
                  isActive
                  expiryCount={expiryCountForUnderlying(selectedUnderlyingMeta.underlying)}
                  onIconError={(e) => { e.target.src = "/images/coin_placeholder.png"; }}
                />
              )}
            </div>

            {showMarketSkeleton ? (
              <OptionsTickerBarSkeleton count={4} />
            ) : hasMarketData && wsUnderlyings.filter((u) => u.symbol !== selectedAsset).map((u) => (
              <OptionsTickerChip
                key={u.symbol}
                underlying={u.underlying}
                symbol={u.symbol}
                iconPath={resolveUnderlyingIconUrl(u.iconPath)}
                price={u.price}
                expiryCount={expiryCountForUnderlying(u.underlying)}
                onClick={() => handleAssetClick(u.symbol)}
                onIconError={(e) => { e.target.src = "/images/coin_placeholder.png"; }}
              />
            ))}
          </div>
          {canScrollTickersRight && (
            <button className="options-scroll-arrow right" onClick={() => scrollTickers(1)}>
              <i className="ri-arrow-right-s-line"></i>
            </button>
          )}

        </div>
        <div className="options-ticker-margin-block">
          <div className="margin-col">
            <div className="margin-row">
              <span className="margin-label dotted">IMR</span>
              {showAccountSkeleton ? (
                <span className="margin-val"><span className="options-sk__bone options-sk__line options-sk__line--val" aria-hidden="true" /></span>
              ) : token ? (
                <>
                  <span className={`margin-val ${optionsAccountPanel?.imrClass || ""}`}>
                    {optionsAccountPanel?.imr ?? "—"}
                  </span>
                  <div className="margin-progress">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${optionsAccountPanel?.imrProgress ?? 0}%`,
                        background: (optionsAccountPanel?.imrPct ?? 0) >= 100 ? "#f6465d" : undefined,
                      }}
                    />
                  </div>
                </>
              ) : (
                <span className="margin-val">—</span>
              )}
            </div>
            <div className="margin-row">
              <span className="margin-label dotted">MMR</span>
              {showAccountSkeleton ? (
                <span className="margin-val"><span className="options-sk__bone options-sk__line options-sk__line--val" aria-hidden="true" /></span>
              ) : token ? (
                <>
                  <span className={`margin-val ${optionsAccountPanel?.mmrClass || ""}`}>
                    {optionsAccountPanel?.mmr ?? "—"}
                  </span>
                  <div className="margin-progress">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${optionsAccountPanel?.mmrProgress ?? 0}%`,
                        background: (optionsAccountPanel?.mmrPct ?? 0) >= 100 ? "#f6465d" : undefined,
                      }}
                    />
                  </div>
                </>
              ) : (
                <span className="margin-val">—</span>
              )}
            </div>
          </div>
          <div className="margin-col">
            <div className="margin-row">
              <span className="margin-label dotted">Margin Balance</span>
              <span className="margin-val">
                {showAccountSkeleton ? (
                  <span className="options-sk__bone options-sk__line options-sk__line--val" aria-hidden="true" />
                ) : (
                  `${fmtAcct("margin_balance")} USD`
                )}
              </span>
            </div>
            <div className="margin-row">
              <span className="margin-label dotted">Available Margin</span>
              <span className="margin-val">
                {showAccountSkeleton ? (
                  <span className="options-sk__bone options-sk__line options-sk__line--val" aria-hidden="true" />
                ) : (
                  `${fmtAcct("available_margin")} USD`
                )}
              </span>
            </div>
          </div>
        </div>
      </div>


      <div className={`mid_option_s d-flex${selectedOption ? " trade-panel-open" : ""}`}>

        <div className="mid_lft_option">
          <div className="options-ai-row" style={{ position: "relative" }}>
            <button
              type="button"
              className={`options-ai-btn ${selectedDate === OPTIONS_EXPIRY_ALL ? "active" : ""}`}
              onClick={() => handleSelectExpiry(OPTIONS_EXPIRY_ALL)}
              title="All expiries"
            >
              All
            </button>
            {canScrollDatesLeft && (
              <button className="options-dates-scroll-arrow left" onClick={() => scrollDates(-1)}>
                <i className="ri-arrow-left-s-line"></i>
              </button>
            )}
            <div
              className="options-dates-scroll"
              ref={datesScrollRef}
              onScroll={updateDatesArrows}
            >
              {showMarketSkeleton ? (
                <OptionsExpiryBarSkeleton count={7} />
              ) : expiryDates.length === 0 ? (
                <span className="text-muted px-2 py-1" style={{ fontSize: "12px" }}>
                  No expiries
                </span>
              ) : (
                <>
                  {expiryDates.map(d => (
                    <button
                      type="button"
                      key={d}
                      className={`options-date-btn ${selectedDate === d ? "active" : ""}`}
                      onClick={() => handleSelectExpiry(d)}
                    >
                      {d}
                    </button>
                  ))}
                </>
              )}
            </div>
            {canScrollDatesRight && (
              <button className="options-dates-scroll-arrow right" onClick={() => scrollDates(1)}>
                <i className="ri-arrow-right-s-line"></i>
              </button>
            )}
          </div>
          <div className="options-filter-row" style={{ position: "relative" }}>
            {canScrollFiltersLeft && (
              <button className="options-filters-scroll-arrow left" onClick={() => scrollFilters(-1)}>
                <i className="ri-arrow-left-s-line"></i>
              </button>
            )}

            <div
              className="options-filters-scroll"
              ref={filtersScrollRef}
              onScroll={updateFiltersArrows}
            >
              <label className="options-filter-group">
                <input
                  type="checkbox"
                  className="options-checkbox"
                  checked={oddSize}
                  onChange={(e) => setOddSize(e.target.checked)}
                />
                Odd Size
              </label>

              <div className="options-filter-group">
                <OptionsAssetSelect
                  selectedAsset={selectedAsset}
                  wsUnderlyings={wsUnderlyings}
                  hasMarketData={hasMarketData}
                  onSelect={handleAssetClick}
                />
              </div>

              <div className="options-filter-group">
                <span>Strike Price</span>
                <input
                  type="text"
                  className="options-input"
                  placeholder="Min"
                  style={{ width: "80px" }}
                  value={strikeMinInput}
                  onChange={(e) => setStrikeMinInput(e.target.value)}
                  aria-label="Minimum strike price"
                />
                <span>-</span>
                <input
                  type="text"
                  className="options-input"
                  placeholder="Max"
                  style={{ width: "80px" }}
                  value={strikeMaxInput}
                  onChange={(e) => setStrikeMaxInput(e.target.value)}
                  aria-label="Maximum strike price"
                />
              </div>

              <button type="button" className="options-reset-btn" onClick={handleFilterReset}>
                Reset
              </button>
            </div>

            {canScrollFiltersRight && (
              <button className="options-filters-scroll-arrow right" onClick={() => scrollFilters(1)}>
                <i className="ri-arrow-right-s-line"></i>
              </button>
            )}
          </div>

          {/* Options Chain Grid */}
          <div className={`options-table-container ${showContractsSkeleton ? 'is-loading-chain' : ''}`}>
            <div className="options-panels-wrapper" style={{ display: 'block' }}>

              {/* STICKY GLOBAL HEADER */}
              <div className="options-global-header" style={{ display: 'flex', position: 'sticky', top: 0, zIndex: 20, background: 'var(--bg-color, #fff)' }}>

                {/* Calls Header */}
                <div className="options-panel calls-panel" style={{ overflowX: 'hidden' }} ref={callsHeaderRef}>
                  <div className="options-panel-inner">
                    <div className="options-panel-header">
                      <div className="options-sub-headers">
                        {activeCols.last && <div className="options-sub-header-cell">Last</div>}
                        {activeCols.vega && <div className="options-sub-header-cell">Vega</div>}
                        {activeCols.theta && <div className="options-sub-header-cell">Theta</div>}
                        {activeCols.gamma && <div className="options-sub-header-cell">Gamma</div>}
                        <div className="options-sub-header-cell">Delta</div>
                        <div className="options-sub-header-cell">Bid/IV</div>
                        <div className="options-sub-header-cell">Mark/IV</div>
                        <div className="options-sub-header-cell">Ask/IV</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strike Header */}
                <div className="options-panel strike-panel">
                  <div className="options-panel-header">
                    <div className="options-sub-headers" style={{ justifyContent: 'center', position: 'relative' }}>
                      <div
                        className="options-sub-header-cell"
                        style={{ textAlign: "center", display: "flex", alignItems: "center", gap: "4px", cursor: "pointer" }}
                        onClick={() => setShowColMenu(!showColMenu)}
                      >
                        Strike Price <i className="ri-equalizer-line" style={{ fontSize: '12px', color: '#848e9c' }}></i>
                      </div>

                      {showColMenu && (
                        <div className="agcex-options-col-menu">
                          {[
                            { key: 'last', label: 'Last' },
                            { key: 'vega', label: 'Vega' },
                            { key: 'theta', label: 'Theta' },
                            { key: 'gamma', label: 'Gamma' },
                          ].map(c => (
                            <div
                              key={c.key}
                              className="agcex-options-col-menu-item"
                              onClick={() => setCols({ ...cols, [c.key]: !cols[c.key] })}
                            >
                              <i
                                className={`agcex-options-col-menu-icon ${cols[c.key] ? "checked ri-checkbox-fill" : "ri-checkbox-blank-line"}`}
                              ></i>
                              {c.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Puts Header */}
                <div className="options-panel puts-panel" style={{ overflowX: 'hidden' }} ref={putsHeaderRef}>
                  <div className="options-panel-inner">
                    <div className="options-panel-header">
                      <div className="options-sub-headers">
                        <div className="options-sub-header-cell">Bid/IV</div>
                        <div className="options-sub-header-cell">Mark/IV</div>
                        <div className="options-sub-header-cell">Ask/IV</div>
                        <div className="options-sub-header-cell">Delta</div>
                        {activeCols.gamma && <div className="options-sub-header-cell">Gamma</div>}
                        {activeCols.theta && <div className="options-sub-header-cell">Theta</div>}
                        {activeCols.vega && <div className="options-sub-header-cell">Vega</div>}
                        {activeCols.last && <div className="options-sub-header-cell">Last</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SCROLLING GLOBAL BODY */}
              {showContractsSkeleton ? (
                <div className="options-global-body options-global-body--skeleton">
                  <OptionsChainSkeleton rows={10} />
                </div>
              ) : displayChains.length === 0 ? (
                <div className="options-global-body options-global-body--empty">
                  <p className="options-chain-empty-msg">{chainEmptyMessage}</p>
                </div>
              ) : (
              <div className="options-global-body" style={{ display: 'flex' }}>

                {/* Calls Body */}
                <div className="options-panel calls-panel" onScroll={handleCallsScroll} ref={callsBodyRef}>
                  <div className="options-panel-inner">
                    <div className="options-panel-body">
                      {displayChains.map(chain => (
                        <React.Fragment key={chain.date}>
                          <div className="agcex-opt-group-header">
                            <div className="agcex-opt-group-title left">Calls</div>
                            <div className="agcex-opt-group-info right">
                              Underlying Forward: {chain.fwd > 0 ? fmt(chain.fwd, 1) : "—"} <span className="agcex-opt-group-divider">|</span> ATM Vol: {chain.vol}
                            </div>
                          </div>
                          {chain.data.map((row, idx) => {
                            const isCallItm = spotPrice != null && row.strike < spotPrice;
                            const showSpotLine = spotPrice != null && idx > 0 && chain.data[idx - 1].strike < spotPrice && row.strike > spotPrice;
                            const isCallSelected = selectedOption?.strike === row.strike && selectedOption?.type === 'call' && selectedOption?.date === chain.date;

                            return (
                              <React.Fragment key={row.strike}>
                                {showSpotLine && <div className="options-forward-line-half"><div className="options-forward-line-left"></div></div>}
                                <div
                                  className={`options-panel-row calls-row ${isCallItm ? "itm" : ""} ${isCallSelected ? "selected" : ""}`}
                                  onClick={() => row.call?.symbol && selectContract({ strike: row.strike, type: 'call', date: chain.date, data: row.call, symbol: row.call.symbol, contractRaw: row.callRaw })}
                                  style={{ cursor: "pointer", position: "relative" }}
                                >
                                  {activeCols.last && <div className="options-cell fw-500">{row.call.last ? fmtOptPrice(row.call.last, 0) : "--"}</div>}
                                  {activeCols.vega && <div className="options-cell">{fmt(row.call.vega, 4)}</div>}
                                  {activeCols.theta && <div className="options-cell">{fmt(row.call.theta, 4)}</div>}
                                  {activeCols.gamma && <div className="options-cell">{fmt(row.call.gamma, 6)}</div>}
                                  <div className="options-cell">{fmt(row.call.delta, 4)}</div>
                                  <div className="options-cell text-green bid-ask">{fmtOptPrice(row.call.bidIv, 0)}</div>
                                  <div className="options-cell text-green"><div>{fmtOptPrice(row.call.markIv, 1)}</div><div className="options-cell-sub">{fmtIvPct(row.call.markIvPct)}</div></div>
                                  <div className="options-cell text-danger bid-ask"><div>{fmtOptPrice(row.call.askIv, 0)}</div><div className="options-cell-sub">{fmtIvPct(row.call.askIvPct)}</div></div>
                                  {isCallSelected && (
                                    <div className="options-row-close-btn" onClick={(e) => { e.stopPropagation(); selectContract(null, { dismissed: true }); }}>
                                      <i className="ri-close-line"></i>
                                    </div>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Strike Body */}
                <div className="options-panel strike-panel">
                  <div className="options-panel-body">
                    {displayChains.map(chain => (
                      <React.Fragment key={chain.date}>
                        <div className="options-group-header-strike" style={{ padding: '8px 0', background: '#f8f9fa', borderBottom: '1px solid #e2e8f0', borderTop: '1px solid #e2e8f0', fontWeight: 'bold', fontSize: '12px', textAlign: 'center' }}>
                          {chain.date}
                        </div>
                        {chain.data.map((row, idx) => {
                          const showSpotLine = spotPrice != null && idx > 0 && chain.data[idx - 1].strike < spotPrice && row.strike > spotPrice;
                          return (
                            <React.Fragment key={row.strike}>
                              {showSpotLine && <div className="options-forward-line-half"><div className="options-forward-label">{fmt(spotPrice, 1)}</div></div>}
                              <div className="options-strike-cell">
                                <div>{fmt(row.strike, 0)}</div>
                                <div className="options-strike-sub">{row.diffPct > 0 ? "+" : ""}{fmt(row.diffPct, 2)}%</div>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* Puts Body */}
                <div className="options-panel puts-panel" onScroll={handlePutsScroll} ref={putsBodyRef}>
                  <div className="options-panel-inner">
                    <div className="options-panel-body">
                      {displayChains.map(chain => (
                        <React.Fragment key={chain.date}>
                          <div className="agcex-opt-group-header">
                            <div className="agcex-opt-group-info left">
                              Time to Expiration: {formatTte(chain.date)}
                            </div>
                            <div className="agcex-opt-group-title right">Puts</div>
                          </div>
                          {chain.data.map((row, idx) => {
                            const isPutItm = spotPrice != null && row.strike > spotPrice;
                            const showSpotLine = spotPrice != null && idx > 0 && chain.data[idx - 1].strike < spotPrice && row.strike > spotPrice;
                            const isPutSelected = selectedOption?.strike === row.strike && selectedOption?.type === 'put' && selectedOption?.date === chain.date;

                            return (
                              <React.Fragment key={row.strike}>
                                {showSpotLine && <div className="options-forward-line-half"><div className="options-forward-line-right"></div></div>}
                                <div
                                  className={`options-panel-row puts-row ${isPutItm ? "itm" : ""} ${isPutSelected ? "selected" : ""}`}
                                  onClick={() => row.put?.symbol && selectContract({ strike: row.strike, type: 'put', date: chain.date, data: row.put, symbol: row.put.symbol, contractRaw: row.putRaw })}
                                  style={{ cursor: "pointer", position: "relative" }}
                                >
                                  {isPutSelected && (
                                    <div className="options-row-close-btn puts-close" onClick={(e) => { e.stopPropagation(); selectContract(null, { dismissed: true }); }}>
                                      <i className="ri-close-line"></i>
                                    </div>
                                  )}
                                  <div className="options-cell text-green bid-ask">{fmtOptPrice(row.put.bidIv, 0)}</div>
                                  <div className="options-cell text-danger"><div>{fmtOptPrice(row.put.markIv, 1)}</div><div className="options-cell-sub">{fmtIvPct(row.put.markIvPct)}</div></div>
                                  <div className="options-cell text-danger bid-ask"><div>{fmtOptPrice(row.put.askIv, 0)}</div><div className="options-cell-sub">{fmtIvPct(row.put.askIvPct)}</div></div>
                                  <div className="options-cell">{fmt(row.put.delta, 4)}</div>
                                  {activeCols.gamma && <div className="options-cell">{fmt(row.put.gamma, 6)}</div>}
                                  {activeCols.theta && <div className="options-cell">{fmt(row.put.theta, 4)}</div>}
                                  {activeCols.vega && <div className="options-cell">{fmt(row.put.vega, 4)}</div>}
                                  {activeCols.last && <div className="options-cell fw-500">{row.put.last ? fmtOptPrice(row.put.last, 0) : "--"}</div>}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>

              </div>
              )}
            </div>
          </div>



        </div>

        {selectedOption && (
          <div className="agcex-options-sidebar-backdrop" onClick={() => selectContract(null, { dismissed: true })}></div>
        )}
        <div className={`mid_right_option${selectedOption ? " is-open" : ""}`}>
          <div className="agcex-new-sidebar-header">
            <div className="agcex-nsh-top">
              <div className="agcex-nsh-title-area">
                <span className="agcex-nsh-title">
                  {selectedOption?.symbol || (selectedOption ? `${selectedAsset}-${selectedOption.date.replace(/-/g, '').slice(2)}-${selectedOption.strike}-${selectedOption.type === 'call' ? 'C' : 'P'}` : "")}
                </span>
                <i className="ri-information-fill agcex-nsh-info"></i>
              </div>
              <div className="agcex-nsh-actions">
                <div
                  className="agcex-nsh-collapse"
                  onClick={() => selectContract(null, { dismissed: true })}
                  style={{ cursor: "pointer" }}
                >
                  <i className="ri-skip-right-line"></i>
                </div>
              </div>
            </div>

            <div className="agcex-nsh-stats">
              <div className="agcex-nsh-stat-col">
                <div className="agcex-nsh-stat-label">
                  <span className="dotted-underline">Index Price</span> <i className="ri-arrow-right-up-line"></i>
                </div>
                <div className="agcex-nsh-stat-val">{spotPrice != null ? fmt(spotPrice, 2) : "—"}</div>
              </div>

              <div className="agcex-nsh-stat-col">
                <div className="agcex-nsh-stat-label">
                  <span className="dotted-underline">Delta</span>
                </div>
                <div className="agcex-nsh-stat-val">{fmt(selectedContractLeg?.delta ?? 0, 4)}</div>
              </div>

              <div className="agcex-nsh-stat-col">
                <div className="agcex-nsh-stat-label">
                  <span className="dotted-underline">Gamma</span>
                </div>
                <div className="agcex-nsh-stat-val">{fmt(selectedContractLeg?.gamma ?? 0, 6)}</div>
              </div>

              <div className="agcex-nsh-stat-col">
                <div className="agcex-nsh-stat-label">
                  <span className="dotted-underline">Theta</span>
                </div>
                <div className="agcex-nsh-stat-val">{fmt(selectedContractLeg?.theta ?? 0, 4)}</div>
              </div>

              <div className="agcex-nsh-stat-col">
                <div className="agcex-nsh-stat-label">
                  <span className="dotted-underline">Vega</span>
                </div>
                <div className="agcex-nsh-stat-val">{fmt(selectedContractLeg?.vega ?? 0, 4)}</div>
              </div>

              <div className="agcex-nsh-stat-col">
                <div className="agcex-nsh-stat-label">Last Price</div>
                <div className="agcex-nsh-stat-val">{obLastPrice != null ? fmtOptPrice(obLastPrice, 2) : "—"}</div>
              </div>
            </div>
          </div>

          {/* --- Options Trade Center (Orderbook + Buy/Sell) --- */}
          <div className="mobile-trade-tabs">
            <div
              className={`mobile-trade-tab ${mobileTradeTab === "buy" ? "active" : ""}`}
              onClick={() => setMobileTradeTab("buy")}
            >Buy/Sell</div>
            <div
              className={`mobile-trade-tab ${mobileTradeTab === "orderbook" ? "active" : ""}`}
              onClick={() => setMobileTradeTab("orderbook")}
            >Order Book</div>

          </div>
          <div className="agcex-options-trade-center">
            <div className={`agcex-trade-col-wrapper ${mobileTradeTab === "orderbook" ? "d-block" : "d-none d-lg-block"}`}>
              <OptionsOrderbook
                obView={obView}
                setObView={setObView}
                askRows={obAskRows}
                bidRows={obBidRows}
                lastPrice={obLastPrice}
                ivPct={obIvPct}
                recentTrades={recentTrades}
                assetLabel={selectedAsset}
              />
            </div>

            <div className={`agcex-trade-col-wrapper ${mobileTradeTab === "buy" ? "d-block" : "d-none d-lg-block"}`}>
              <OptionsOrderForm
                orderSide={orderSide}
                setOrderSide={setOrderSide}
                token={token}
                accountUpdate={accountUpdate}
                obLastPrice={obLastPrice}
                obIvPct={obIvPct}
                selectedAsset={selectedAsset}
                selectedContractSymbol={selectedContractSymbol}
                selectedContractRaw={selectedContractRaw ?? selectedOption?.contractRaw ?? null}
                tickSize={activeContractTickSize}
                stepSize={activeContractStepSize}
                indexPrice={indexPrice}
                feeRates={marketOverview?.fee_rates}
                symbolPosition={symbolPosition}
                symbolOpenOrders={symbolOpenOrders}
                onTransferClick={() => setShowTransferModal(true)}
                onOrderAcknowledged={refreshLiveTradeChannels}
              />
            </div>
          </div>

          {/* PnL Preview / Mark Price / Index Price chart tabs — hidden for now */}
          {false && (
            <div className="agcex-opt-chart-tabs-container">
              {/* Tab Header */}
              <div className="agcex-opt-chart-tabs-header">
                <div
                  className={`agcex-opt-chart-tab ${tradeCenterChartTab === "pnl" ? "active" : ""}`}
                  onClick={() => setTradeCenterChartTab("pnl")}
                >PnL Preview</div>
                <div
                  className={`agcex-opt-chart-tab ${tradeCenterChartTab === "mark" ? "active" : ""}`}
                  onClick={() => setTradeCenterChartTab("mark")}
                >Mark Price</div>
                <div
                  className={`agcex-opt-chart-tab ${tradeCenterChartTab === "index" ? "active" : ""}`}
                  onClick={() => setTradeCenterChartTab("index")}
                >Index Price</div>
                <div style={{ flex: 1 }}></div>
                <div className="agcex-opt-chart-refresh">
                  <i className="ri-refresh-line"></i>
                </div>
              </div>

              {/* Tab Body */}
              <div className="agcex-opt-chart-body">
                {tradeCenterChartTab === "pnl" && (
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <div className="agcex-opt-chart-pnl-header">
                      <div className="agcex-opt-chart-pnl-legend">
                        <div className="agcex-opt-chart-pnl-legend-item"><div style={{ width: "16px", height: "2px", background: "#2ebd85" }}></div> Day0</div>
                        <div className="agcex-opt-chart-pnl-legend-item"><div style={{ width: "16px", height: "0", borderTop: "2px dashed #2962ff" }}></div> Expiration Date</div>
                      </div>
                      <div className="agcex-opt-chart-pnl-controls" style={{ position: "relative" }}>
                        <div className="agcex-opt-chart-pnl-iv">IV</div>
                        <div className="agcex-opt-chart-pnl-mark" onClick={() => setShowMarkSlider(!showMarkSlider)} style={{ cursor: "pointer" }}>
                          {markSliderValue === 0 ? "Mark" : `${markSliderValue > 0 ? '+' : ''}${markSliderValue}% Mark`} <i className="ri-pencil-fill" style={{ fontSize: "10px", color: "#848e9c" }}></i>
                        </div>

                        {showMarkSlider && (
                          <div className="agcex-opt-mark-slider-popup">
                            <div className="agcex-opt-mark-slider-wrapper">
                              <input
                                type="range"
                                min="-100"
                                max="300"
                                value={markSliderValue}
                                onChange={(e) => setMarkSliderValue(Number(e.target.value))}
                                className="agcex-opt-mark-slider-input"
                                style={{ background: `linear-gradient(to right, var(--text-color, #1e2329) 0%, var(--text-color, #1e2329) ${(markSliderValue + 100) / 4}%, var(--border-color, #e2e8f0) ${(markSliderValue + 100) / 4}%, var(--border-color, #e2e8f0) 100%)` }}
                              />
                            </div>
                            <div className="agcex-opt-mark-slider-labels">
                              <span>-100% Mark</span>
                              <span>+300%</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="agcex-opt-chart-pnl-slider-row">
                      <div className="agcex-opt-chart-pnl-badge">D+0</div>
                      <div className="agcex-opt-chart-pnl-slider">
                        Today <i className="ri-checkbox-blank-circle-line" style={{ fontSize: "12px", margin: "0 4px", color: "#848e9c", fontWeight: "bold" }}></i>
                        <div style={{ height: "4px", background: "#e2e8f0", flex: 1, borderRadius: "2px" }}></div>
                        <span style={{ marginLeft: "8px" }}>Expiration Date</span>
                      </div>
                    </div>

                    {/* Graph Placeholder */}
                    <div className="agcex-opt-chart-pnl-graph">

                      {/* Y Axis labels */}
                      <div className="agcex-opt-chart-pnl-y-axis">
                        <span>400</span><span>300</span><span>200</span><span>100</span><span>0</span><span>-100</span><span>-200</span>
                      </div>

                      {/* Horizontal Grid Lines */}
                      {[0, 1, 2, 3, 4, 5, 6].map(i => (
                        <div key={i} className="agcex-opt-chart-pnl-grid-line" style={{ top: `${(i / 6) * 100}%` }}></div>
                      ))}

                      {/* X Axis labels */}
                      <div className="agcex-opt-chart-pnl-x-axis">
                        <span>1,600</span><span>1,700</span><span>1,800</span><span>1,900</span><span>2,000</span><span>2,100</span><span>2,200</span>
                      </div>

                      {/* Current Index Marker */}
                      <div className="agcex-opt-chart-pnl-marker">
                        <div className="agcex-opt-chart-pnl-marker-label">Index 2,118.6</div>
                      </div>

                      {/* Bottom Slider UI */}
                      <div className="agcex-opt-chart-pnl-bottom-slider">
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#848e9c", marginBottom: "4px" }}>
                          <span>0</span><span>6,300</span>
                        </div>
                        <div style={{ height: "4px", background: "#e2e8f0", borderRadius: "2px", position: "relative" }}>
                          <div style={{ position: "absolute", left: "30%", right: "60%", height: "4px", background: "#1e2329", zIndex: 2 }}></div>
                          <div style={{ position: "absolute", left: "29%", top: "-3px", width: "10px", height: "10px", borderRadius: "50%", border: "2px solid #1e2329", background: "#fff", zIndex: 3 }}></div>
                          <div style={{ position: "absolute", right: "59%", top: "-3px", width: "10px", height: "10px", borderRadius: "50%", border: "2px solid #1e2329", background: "#fff", zIndex: 3 }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {tradeCenterChartTab === "mark" && (
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    {/* Timeframes */}
                    <div className="agcex-opt-chart-timeframes">
                      <span className="agcex-opt-chart-tf-item">1m</span><span className="agcex-opt-chart-tf-item">3m</span><span className="agcex-opt-chart-tf-item">5m</span><span className="agcex-opt-chart-tf-item">15m</span><span className="agcex-opt-chart-tf-item">30m</span>
                      <span className="agcex-opt-chart-tf-item">1h</span><span className="agcex-opt-chart-tf-item">2h</span><span className="agcex-opt-chart-tf-item">4h</span><span className="agcex-opt-chart-tf-item">6h</span><span className="agcex-opt-chart-tf-item">12h</span>
                      <span className="agcex-opt-chart-tf-item active">1D</span>
                      <span className="agcex-opt-chart-tf-item">1W</span><span className="agcex-opt-chart-tf-item">1M</span>
                    </div>

                    {/* Legend */}
                    <div className="agcex-opt-chart-legend-row">
                      <span style={{ color: "#f6465d" }}>O=228.20</span>
                      <span style={{ color: "#f6465d" }}>H=255.07</span>
                      <span style={{ color: "#f6465d" }}>L=207.83</span>
                      <span style={{ color: "#f6465d" }}>C=215.85</span>
                      <span style={{ color: "#f6465d" }}>-12.34(-5.41%)</span>
                    </div>
                    <div className="agcex-opt-chart-ma-row">
                      <span style={{ color: "#848e9c" }}>MA(7,14,28)</span>
                      <span style={{ color: "#f38a36" }}>MA7: 247.61</span>
                      <span style={{ color: "#8e44ad" }}>MA14: 330.08</span>
                      <span style={{ color: "#2980b9" }}>MA28: n/a</span>
                    </div>

                    {/* Placeholder Chart Area */}
                    <div className="agcex-opt-chart-placeholder">
                      <div className="agcex-opt-chart-y-axis">
                        <span>540.00</span><span>480.00</span><span>420.00</span><span>360.00</span><span>300.00</span><span>240.00</span><span>180.00</span>
                      </div>
                      <div className="agcex-opt-chart-x-axis">
                        <span>04-30</span><span>2026-05</span><span>05-18</span>
                      </div>
                      {/* Simulated current price line */}
                      <div className="agcex-opt-chart-current-price">
                        <div className="agcex-opt-chart-current-price-label">215.85</div>
                      </div>
                      <span style={{ color: "#b7bdc6", fontSize: "14px" }}>[ Mark Price Candlestick Chart Rendered Here ]</span>
                    </div>
                  </div>
                )}

                {tradeCenterChartTab === "index" && (
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    {/* Timeframes */}
                    <div className="agcex-opt-chart-timeframes">
                      <span className="agcex-opt-chart-tf-item">1m</span><span className="agcex-opt-chart-tf-item">3m</span><span className="agcex-opt-chart-tf-item">5m</span><span className="agcex-opt-chart-tf-item">15m</span><span className="agcex-opt-chart-tf-item">30m</span>
                      <span className="agcex-opt-chart-tf-item">1h</span><span className="agcex-opt-chart-tf-item">2h</span><span className="agcex-opt-chart-tf-item">4h</span><span className="agcex-opt-chart-tf-item">6h</span><span className="agcex-opt-chart-tf-item">12h</span>
                      <span className="agcex-opt-chart-tf-item active">1D</span>
                      <span className="agcex-opt-chart-tf-item">1W</span><span className="agcex-opt-chart-tf-item">1M</span>
                    </div>

                    {/* Legend */}
                    <div className="agcex-opt-chart-legend-row">
                      <span style={{ color: "#f6465d" }}>O=2,129.48</span>
                      <span style={{ color: "#f6465d" }}>H=2,156.83</span>
                      <span style={{ color: "#f6465d" }}>L=2,110.01</span>
                      <span style={{ color: "#f6465d" }}>C=2,118.66</span>
                      <span style={{ color: "#f6465d" }}>-10.82(-0.51%)</span>
                    </div>
                    <div className="agcex-opt-chart-ma-row">
                      <span style={{ color: "#848e9c" }}>MA(7,14,28)</span>
                      <span style={{ color: "#f38a36" }}>MA7: 2,146.63</span>
                      <span style={{ color: "#8e44ad" }}>MA14: 2,227.63</span>
                      <span style={{ color: "#2980b9" }}>MA28: 2,270.72</span>
                    </div>

                    {/* Placeholder Chart Area */}
                    <div className="agcex-opt-chart-placeholder">
                      <div className="agcex-opt-chart-y-axis">
                        <span>2,480.00</span><span>2,400.00</span><span>2,320.00</span><span>2,240.00</span><span>2,160.00</span><span>2,080.00</span><span>2,000.00</span>
                      </div>
                      <div className="agcex-opt-chart-x-axis">
                        <span>04</span><span>04-12</span><span>04-20</span><span>04-28</span><span>2026-05</span><span>05-14</span>
                      </div>
                      {/* Simulated current price line */}
                      <div className="agcex-opt-chart-current-price">
                        <div className="agcex-opt-chart-current-price-label">2,118.66</div>
                      </div>
                      <span style={{ color: "#b7bdc6", fontSize: "14px" }}>[ Index Price Candlestick Chart Rendered Here ]</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>

      <div className="trade_account_summary_assets options_bordertop">

            <div className="trade_account_summary_assets mt-0">

              <div className="trade_summary_table_lft mt-0 position_order trade_summary_table_lft_position_order">
                <div className="top_th_easyop border-0 spot_orders_top">
                  <SpotOrdersPrimaryTabsScroll
                    positionOrderTab={positionOrderTab}
                    setPositionOrderTab={setPositionOrderTab}
                    positionsCount={openPositions.length}
                    openOrdersCount={openOrders.length}
                  />
                </div>

                {positionOrderTab === "openOrders" ? (
                  <SpotOpenOrdersFilterRow
                    SPOT_OPEN_ORDER_KINDS={SPOT_OPEN_ORDER_KINDS}
                    openOrderKindTab={openOrderKindTab}
                    setOpenOrderKindTab={setOpenOrderKindTab}
                    orderType={orderType}
                    setorderType={setorderType}
                  />
                ) : null}

                {positionOrderTab === "positions" && (
                  <div className="cnt_table positions active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                    <table className="table table_home spot_orders_table agcex-opt-positions-table">
                          <thead>
                            <tr>
                          <th>Expiry Date</th>
                              <th>Symbol</th>
                          <th>Long/Short</th>
                          <th>Amount</th>
                          <th>Strike Price</th>
                              <th>Entry Price</th>
                              <th>Mark Price</th>
                              <th>Unrealized PnL</th>
                              <th>Market Value</th>
                              <th className="yellowcolor">Close Position</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!token ? (
                          <tr><td colSpan={10} className="text-center text-muted py-4">Sign in to view open positions</td></tr>
                            ) : openPositionsLoading && openPositions.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={10} loading />
                            ) : openPositions.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={10} />
                            ) : openPositions.map((row) => {
                          const view = mapOptionsOpenPositionRowView(row, openOrders);
                              return (
                            <tr key={view.key}>
                              <td className="agcex-opt-pos-muted">{view.expiryDate}</td>
                              <td>
                                <OptionsPositionSymbolCell
                                  symbolDisplay={view.symbolDisplay}
                                  sideAccent={view.sideAccent}
                                />
                                  </td>
                              <td className={view.side.className}>{view.side.label}</td>
                              <td>{view.amount}</td>
                              <td>{view.strikePrice}</td>
                              <td>{view.entryPrice}</td>
                              <td>{view.markPrice}</td>
                              <td className={view.pnl.className}>{view.pnl.text}</td>
                              <td>{view.marketValue != null ? fmt(view.marketValue, 2) : "—"}</td>
                                  <td className="agcex-opt-close-pos-cell">{renderOptionsClosePositionActions(row)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order_history_mobile_view twomobile so-mobile">
                      {!token ? (
                        <div className="text-center text-muted py-4">Sign in to view open positions</div>
                      ) : openPositions.length === 0 ? (
                    <OptionsHistoryEmpty mobile />
                      ) : openPositions.map((row) => {
                    const view = mapOptionsOpenPositionRowView(row, openOrders);
                        return (
                          <OrderMobileCardShell
                        key={view.key}
                        desktopColumnCount={10}
                            popupTitle="Position details"
                        heroPrimary={view.symbolDisplay.primary}
                        heroSecondary={`${view.symbolDisplay.secondary} · ${view.side.label}`}
                          >
                            <div className="so-mobileCard__row">
                          <span>Expiry Date</span>
                          <span>{view.expiryDate}</span>
                        </div>
                        <div className="so-mobileCard__row">
                          <span>Long/Short</span>
                          <span className={view.side.className}>{view.side.label}</span>
                        </div>
                        <div className="so-mobileCard__row">
                          <span>Amount</span>
                          <span>{view.amount}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Available</span>
                          <span>{view.available}</span>
                        </div>
                        <div className="so-mobileCard__row">
                          <span>Strike Price</span>
                          <span>{view.strikePrice}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Entry Price</span>
                          <span>{view.entryPrice}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Mark Price</span>
                          <span>{view.markPrice}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Unrealized PnL</span>
                          <span className={view.pnl.className}>{view.pnl.text}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Market Value</span>
                          <span>{view.marketValue != null ? fmt(view.marketValue, 2) : "—"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span className="yellowcolor">Close</span>
                              <span>{renderOptionsClosePositionActions(row)}</span>
                            </div>
                          </OrderMobileCardShell>
                        );
                      })}
                    </div>
                  </div>
                )}

                {positionOrderTab === "positionHistory" && (
                  <div className="cnt_table positionHistory active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                    <table className="table table_home spot_orders_table agcex-opt-positions-table">
                          <thead>
                            <tr>
                          <th>Expiry Date</th>
                              <th>Symbol</th>
                          <th>Long/Short</th>
                          <th>Amount</th>
                          <th>Strike Price</th>
                              <th>Avg Price</th>
                              <th>Open Notional</th>
                              <th>Realized PnL</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!token ? (
                              <tr><td colSpan={9} className="text-center text-muted py-4">Sign in to view position history</td></tr>
                            ) : positionHistory.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={9} />
                            ) : positionHistory.map((row) => {
                              const view = mapOptionsPositionHistoryRowView(row);
                              return (
                              <tr key={view.key}>
                              <td className="agcex-opt-pos-muted">{view.expiryDate || view.closedAt}</td>
                              <td>
                                <OptionsPositionSymbolCell
                                  symbolDisplay={view.symbolDisplay}
                                  sideAccent={view.sideAccent}
                                />
                              </td>
                                <td className={view.side.className}>{view.side.label}</td>
                              <td title={view.sizeTitle}>{view.amount}</td>
                              <td>{view.strikePrice}</td>
                                <td>{view.avgPrice}</td>
                                <td>{view.openNotional}</td>
                                <td className={view.pnl.className}>{view.pnl.text}</td>
                                <td className={view.status.className}>{view.status.label}</td>
                              </tr>
                          );
                        })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order_history_mobile_view twomobile so-mobile">
                      <div className="d-flex datalist_mbl flex-column">
                        {!token ? (
                          <div className="text-center text-muted py-4">Sign in to view position history</div>
                        ) : positionHistory.length === 0 ? (
                      <OptionsHistoryEmpty mobile />
                        ) : positionHistory.map((row) => {
                          const view = mapOptionsPositionHistoryRowView(row);
                          return (
                          <OrderMobileCardShell
                            key={view.key}
                            desktopColumnCount={9}
                            popupTitle="Position history details"
                          heroPrimary={view.symbolDisplay.primary}
                          heroSecondary={`${view.symbolDisplay.secondary} · ${view.side.label}`}
                          >
                            <div className="so-mobileCard__row">
                            <span>Expiry Date</span>
                            <span>{view.expiryDate || view.closedAt}</span>
                            </div>
                            <div className="so-mobileCard__row">
                            <span>Long/Short</span>
                            <span className={view.side.className}>{view.side.label}</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Amount</span>
                            <span className="so-num" title={view.sizeTitle}>{view.amount}</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Strike Price</span>
                            <span>{view.strikePrice}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Avg Price</span>
                              <span className="so-num">{view.avgPrice}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Open Notional</span>
                              <span>{view.openNotional}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Realized PnL</span>
                              <span className={view.pnl.className}>{view.pnl.text}</span>
                            </div>
                              <div className="so-mobileCard__row">
                            <span>Status</span>
                            <span className={view.status.className}>{view.status.label}</span>
                            </div>
                          </OrderMobileCardShell>
                      );
                    })}
                      </div>
                    </div>
                  </div>
                )}

                {positionOrderTab === "openOrders" && (
                  <div className="cnt_table openOrders active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                        <table className="table table_home spot_orders_table">
                          <thead>
                            <tr>
                              <th>Symbol</th>
                              <th>Creation Time</th>
                              <th>Side</th>
                              <th>Price</th>
                              <th>Amount</th>
                              <th>Filled</th>
                              <th>Unfilled</th>
                              <th>Fill Price</th>
                              <th>Reduce Only</th>
                              <th>Type</th>
                              <th>TIF</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!token ? (
                              <tr><td colSpan={13} className="text-center text-muted py-4">Sign in to view open orders</td></tr>
                        ) : openOrdersLoading && openOrders.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={13} loading />
                        ) : filteredOpenOrders.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={13} />
                        ) : filteredOpenOrders.map((item) => {
                                const side = String(item.side || "").toUpperCase();
                                const sideLabel = side === "BUY" ? "Buy" : side === "SELL" ? "Sell" : side;
                                const reduceOnly = isReduceOnlyIntent(item.order_intent);
                                const priceDisplay = formatOptionsOpenOrderPrice(item);
                                const avgFill = optionsOpenOrderAvgFill(item);
                                const cancelKey = `cancel:${item.order_id}`;
                                const cancelDisabled = isActionCoolingDown(cancelKey);
                                return (
                              <tr key={item.order_id || item.symbol}>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <div className="fw-bold">{item.symbol}</div>
                                  </div>
                                  {item.order_intent ? (
                                    <div className="small text-muted">{formatOptionsOrderIntent(item.order_intent)}</div>
                                  ) : null}
                                </td>
                                <td>{fmtTs(item.created_at)}</td>
                                <td className={sideLabel === "Buy" ? "agcex-spot-side-buy" : "agcex-spot-side-sell"}>
                                  {sideLabel}
                                </td>
                                <td>{priceDisplay}</td>
                                <td>{decNum(item.quantity)}</td>
                                <td>{decNum(item.filled_quantity)}</td>
                                <td>{decNum(item.remaining_quantity)}</td>
                                <td>{avgFill}</td>
                                <td>{reduceOnly ? "Yes" : "No"}</td>
                                <td>{formatOptionsOrderType(item.order_type)}</td>
                                <td>{item.time_in_force || "GTC"}</td>
                                <td className={optionsOrderStatusClass(item.status)}>{formatOptionsOrderStatus(item.status)}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-dark btn-sm rounded-pill px-3 static-action-btn"
                                    disabled={cancelDisabled}
                                    onClick={() => handleCancelOptionOrder(item.order_id)}
                                  >
                                    Cancel
                                  </button>
                                </td>
                              </tr>
                          );
                        })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order_history_mobile_view twomobile so-mobile">
                      <div className="d-flex datalist_mbl flex-column">
                        {!token ? (
                          <div className="text-center text-muted py-4">Sign in to view open orders</div>
                    ) : openOrdersLoading && openOrders.length === 0 ? (
                      <OptionsHistoryEmpty mobile loading />
                    ) : filteredOpenOrders.length === 0 ? (
                      <OptionsHistoryEmpty mobile />
                    ) : filteredOpenOrders.map((item) => {
                            const side = String(item.side || "").toUpperCase();
                            const sideLabel = side === "BUY" ? "Buy" : side === "SELL" ? "Sell" : side;
                            const reduceOnly = isReduceOnlyIntent(item.order_intent);
                            const priceDisplay = formatOptionsOpenOrderPrice(item);
                            const cancelKey = `cancel:${item.order_id}`;
                            const cancelDisabled = isActionCoolingDown(cancelKey);
                            return (
                          <OrderMobileCardShell
                            key={item.order_id || item.symbol}
                            desktopColumnCount={13}
                            popupTitle="Order details"
                            heroPrimary={item.symbol}
                            heroSecondary={`${sideLabel} · ${formatOptionsOrderType(item.order_type)} · ${formatOptionsOrderStatus(item.status)}`}
                          >
                            <div className="so-mobileCard__row">
                              <span>Created</span>
                              <span>{fmtTs(item.created_at)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Price</span>
                              <span className="so-num">{priceDisplay}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Amount</span>
                              <span className="so-num">{decNum(item.quantity)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Filled</span>
                              <span className="so-num">{decNum(item.filled_quantity)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Unfilled</span>
                              <span className="so-num">{decNum(item.remaining_quantity)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Intent</span>
                              <span>{formatOptionsOrderIntent(item.order_intent)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>TIF</span>
                              <span>{item.time_in_force || "GTC"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Reduce Only</span>
                              <span>{reduceOnly ? "Yes" : "No"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Action</span>
                              <span>
                                <button
                                  type="button"
                                  className="so-cancelBtn static-action-btn"
                                  disabled={cancelDisabled}
                                  onClick={() => handleCancelOptionOrder(item.order_id)}
                                >
                                  Cancel
                                </button>
                              </span>
                            </div>
                          </OrderMobileCardShell>
                      );
                    })}
                      </div>
                    </div>
                  </div>
                )}

                {positionOrderTab === "tradeHistory" && (
                  <div className="cnt_table tradeHistory active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                        <table className="table table_home spot_orders_table">
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Symbol</th>
                              <th>Side</th>
                              <th>Role</th>
                              <th>Price</th>
                              <th>Qty</th>
                              <th>Fee</th>
                              <th>Total</th>
                              <th>Trade ID</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!token ? (
                              <tr><td colSpan={9} className="text-center text-muted py-4">Sign in to view trade history</td></tr>
                            ) : tradeHistory.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={9} />
                            ) : tradeHistory.map((row) => {
                              const view = resolveOptionsTradeUserView(row, userId);
                              const price = decNum(row.price);
                              const qty = decNum(row.quantity);
                              const total = decNum(row.trade_value) || (price > 0 && qty > 0 ? price * qty : 0);
                              const feeStr = view.fee > 0
                                ? `${view.fee.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })} ${view.feeAsset}`
                                : "—";
                              return (
                              <tr key={row.trade_id || row._id || row.id}>
                                <td>{fmtTs(row.created_at)}</td>
                                <td className="fw-bold">{row.symbol || "—"}</td>
                                <td className={view.sideClass}>{view.side || "—"}</td>
                                <td>{view.role}</td>
                                <td>{price > 0 ? decStr(row.price) : "—"}</td>
                                <td>{qty > 0 ? decStr(row.quantity) : "—"}</td>
                                <td>{feeStr}</td>
                                <td>{total > 0 ? `${decStr(row.trade_value) || fmt(total, 4)} USDT` : "—"}</td>
                                <td title={row.trade_id || ""}>{formatOptionsTradeId(row.trade_id)}</td>
                              </tr>
                          );
                        })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order_history_mobile_view twomobile so-mobile">
                      <div className="d-flex datalist_mbl flex-column">
                        {!token ? (
                          <div className="text-center text-muted py-4">Sign in to view trade history</div>
                        ) : tradeHistory.length === 0 ? (
                      <OptionsHistoryEmpty mobile />
                        ) : tradeHistory.map((row) => {
                          const view = resolveOptionsTradeUserView(row, userId);
                          const price = decNum(row.price);
                          const qty = decNum(row.quantity);
                          const total = decNum(row.trade_value) || (price > 0 && qty > 0 ? price * qty : 0);
                          const feeStr = view.fee > 0
                            ? `${view.fee.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })} ${view.feeAsset}`
                            : "—";
                          return (
                          <OrderMobileCardShell
                            key={row.trade_id || row._id || row.id}
                            desktopColumnCount={9}
                            popupTitle="Trade details"
                            heroPrimary={row.symbol}
                            heroSecondary={`${view.side || "—"}${view.role !== "—" ? ` · ${view.role}` : ""}`}
                          >
                            <div className="so-mobileCard__row">
                              <span>Time</span>
                              <span>{fmtTs(row.created_at)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Price</span>
                              <span className="so-num">{price > 0 ? decStr(row.price) : "—"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Qty</span>
                              <span className="so-num">{qty > 0 ? decStr(row.quantity) : "—"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Fee</span>
                              <span>{feeStr}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Total</span>
                              <span>{total > 0 ? `${decStr(row.trade_value) || fmt(total, 4)} USDT` : "—"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Trade ID</span>
                              <span>{formatOptionsTradeId(row.trade_id)}</span>
                            </div>
                          </OrderMobileCardShell>
                      );
                    })}
                      </div>
                    </div>
                  </div>
                )}

                {positionOrderTab === "transactionHistory" && (
                  <div className="cnt_table transactionHistory active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                        <table className="table table_home spot_orders_table">
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Type</th>
                          <th>Direction</th>
                              <th>Amount</th>
                              <th>Asset</th>
                          <th>Symbol</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                        {!token ? (
                          <tr><td colSpan={7} className="text-center text-muted py-4">Sign in to view transaction history</td></tr>
                        ) : historyLoading && transactionHistory.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={7} loading />
                        ) : transactionHistory.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={7} />
                        ) : transactionHistory.map((row) => {
                          const dirFmt = formatOptionsTransactionDirection(row.direction);
                          const amtFmt = formatOptionsTransactionAmount(row.amount, row.direction);
                          const asset = row.asset || "USDT";
                          return (
                            <tr key={optionsTransactionRowKey(row)}>
                                <td>{fmtTs(row.created_at || row.timestamp)}</td>
                              <td>{formatOptionsTransactionType(row.transaction_type || row.type)}</td>
                              <td className={dirFmt.className}>{dirFmt.label}</td>
                              <td className={amtFmt.className}>{amtFmt.text} {asset}</td>
                              <td>{asset}</td>
                              <td className="fw-bold">{row.symbol || "—"}</td>
                              <td>{row.description || "—"}</td>
                              </tr>
                          );
                        })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                <div className="order_history_mobile_view twomobile so-mobile">
                  <div className="d-flex datalist_mbl flex-column">
                    {!token ? (
                      <div className="text-center text-muted py-4">Sign in to view transaction history</div>
                    ) : historyLoading && transactionHistory.length === 0 ? (
                      <OptionsHistoryEmpty mobile loading />
                    ) : transactionHistory.length === 0 ? (
                      <OptionsHistoryEmpty mobile />
                    ) : transactionHistory.map((row) => {
                      const dirFmt = formatOptionsTransactionDirection(row.direction);
                      const amtFmt = formatOptionsTransactionAmount(row.amount, row.direction);
                      const asset = row.asset || "USDT";
                      const typeLabel = formatOptionsTransactionType(row.transaction_type || row.type);
                      return (
                        <OrderMobileCardShell
                          key={optionsTransactionRowKey(row)}
                          desktopColumnCount={7}
                          popupTitle="Transaction details"
                          heroPrimary={typeLabel}
                          heroSecondary={`${dirFmt.label} · ${amtFmt.text} ${asset}`}
                        >
                          <div className="so-mobileCard__row">
                            <span>Time</span>
                            <span>{fmtTs(row.created_at || row.timestamp)}</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Direction</span>
                            <span className={dirFmt.className}>{dirFmt.label}</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Amount</span>
                            <span className={amtFmt.className}>{amtFmt.text} {asset}</span>
                          </div>
                          {row.symbol ? (
                            <div className="so-mobileCard__row">
                              <span>Symbol</span>
                              <span>{row.symbol}</span>
                            </div>
                          ) : null}
                          <div className="so-mobileCard__row">
                            <span>Description</span>
                            <span>{row.description || "—"}</span>
                          </div>
                        </OrderMobileCardShell>
                      );
                    })}
                  </div>
                </div>
                  </div>
                )}

                {/* Bots tab — hidden
                {positionOrderTab === "bots" && (
                  <div className="cnt_table bots active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                        <table className="table table_home spot_orders_table">
                          <thead>
                            <tr>
                              <th>Bot Name</th>
                              <th>Strategy</th>
                              <th>Runtime</th>
                              <th>Total Profit</th>
                              <th>Status</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>BTC Grid Bot</td>
                              <td>Neutral Grid</td>
                              <td>2d 14h</td>
                              <td className="agcex-spot-side-buy">+45.20 USDT</td>
                              <td>Running</td>
                              <td>
                                <button className="btn btn-dark btn-sm rounded-pill px-3 static-action-btn">Stop</button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order_history_mobile_view twomobile so-mobile">
                      <div className="d-flex datalist_mbl flex-column">
                        <OrderMobileCardShell
                          desktopColumnCount={6}
                          popupTitle="Bot details"
                          heroPrimary="BTC Grid Bot"
                          heroSecondary="Running"
                        >
                          <div className="so-mobileCard__row">
                            <span>Strategy</span>
                            <span>Neutral Grid</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Runtime</span>
                            <span>2d 14h</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Total Profit</span>
                            <span className="agcex-spot-side-buy">+45.20 USDT</span>
                          </div>
                          <div className="so-mobileCard__row">
                            <span>Action</span>
                            <span>
                              <button className="so-cancelBtn static-action-btn">Stop</button>
                            </span>
                          </div>
                        </OrderMobileCardShell>
                      </div>
                    </div>
                  </div>
                )}
                */}

                {positionOrderTab === "orderHistory" && (
                  <div className="cnt_table orderHistory active">
                    <div className="desktop_view2">
                      <div className="table-responsive spot_orders_table_wrap static-table-wrap">
                        <table className="table table_home spot_orders_table">
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Symbol</th>
                              <th>Side</th>
                              <th>Type</th>
                              <th>Price</th>
                              <th>Avg Fill</th>
                              <th>Qty</th>
                              <th>Filled</th>
                              <th>Total</th>
                              <th>Reduce Only</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!token ? (
                              <tr><td colSpan={11} className="text-center text-muted py-4">Sign in to view order history</td></tr>
                            ) : orderHistory.length === 0 ? (
                          <OptionsHistoryEmpty colSpan={11} />
                            ) : orderHistory.map((row) => {
                              const sideFmt = formatOptionsOrderHistorySide(row.side);
                              const priceDisplay = formatOptionsOpenOrderPrice(row);
                              const avgFill = formatOptionsOrderHistoryAvgFill(row);
                              const reduceOnly = isReduceOnlyIntent(row.order_intent);
                              const executed = decNum(row.executed_value);
                              const statusLabel = formatOptionsOrderStatus(row.status);
                              const rejectReason = row.reject_reason ? String(row.reject_reason) : null;
                              return (
                              <tr key={row.order_id || row._id || row.id}>
                                <td>{fmtTs(row.updated_at || row.created_at)}</td>
                                <td className="fw-bold">{row.symbol || "—"}</td>
                                <td className={sideFmt.className}>{sideFmt.label}</td>
                                <td>{formatOptionsOrderType(row.order_type)}</td>
                                <td>{priceDisplay}</td>
                                <td>{avgFill}</td>
                                <td>{decNum(row.quantity) > 0 ? decStr(row.quantity) : "—"}</td>
                                <td>{decNum(row.filled_quantity) > 0 ? decStr(row.filled_quantity) : "0"}</td>
                                <td>{executed > 0 ? `${decStr(row.executed_value)} USDT` : "—"}</td>
                                <td>{reduceOnly ? "Yes" : "No"}</td>
                                <td className={optionsOrderStatusClass(row.status)}>
                                  <div>{statusLabel}</div>
                                  {rejectReason ? (
                                    <div className="small text-muted">{rejectReason}</div>
                                  ) : null}
                                </td>
                              </tr>
                          );
                        })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="order_history_mobile_view twomobile so-mobile">
                      <div className="d-flex datalist_mbl flex-column">
                        {!token ? (
                          <div className="text-center text-muted py-4">Sign in to view order history</div>
                        ) : orderHistory.length === 0 ? (
                      <OptionsHistoryEmpty mobile />
                        ) : orderHistory.map((row) => {
                          const sideFmt = formatOptionsOrderHistorySide(row.side);
                          const priceDisplay = formatOptionsOpenOrderPrice(row);
                          const avgFill = formatOptionsOrderHistoryAvgFill(row);
                          const reduceOnly = isReduceOnlyIntent(row.order_intent);
                          const executed = decNum(row.executed_value);
                          return (
                          <OrderMobileCardShell
                            key={row.order_id || row._id || row.id}
                            desktopColumnCount={11}
                            popupTitle="Order history details"
                            heroPrimary={row.symbol}
                            heroSecondary={`${sideFmt.label} · ${formatOptionsOrderType(row.order_type)} · ${formatOptionsOrderStatus(row.status)}`}
                          >
                            <div className="so-mobileCard__row">
                              <span>Time</span>
                              <span>{fmtTs(row.updated_at || row.created_at)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Price</span>
                              <span className="so-num">{priceDisplay}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Avg Fill</span>
                              <span className="so-num">{avgFill}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Qty</span>
                              <span>{decStr(row.quantity)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Filled</span>
                              <span>{decStr(row.filled_quantity)}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Total</span>
                              <span>{executed > 0 ? `${decStr(row.executed_value)} USDT` : "—"}</span>
                            </div>
                            <div className="so-mobileCard__row">
                              <span>Reduce Only</span>
                              <span>{reduceOnly ? "Yes" : "No"}</span>
                            </div>
                            {/* // {row.reject_reason ? (
                            //   <div className="so-mobileCard__row">
                            //     <span>Reason</span>
                            //     <span>{row.reject_reason}</span>
                            //   </div>
                            // ) : null} */}
                          </OrderMobileCardShell>
                      );
                    })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

        <div className="assets_right d-none d-lg-block assets_panel_desktop option_assetsright">
          <OptionsAccountPanel
            panel={optionsAccountPanel}
            showAccountSkeleton={showAccountSkeleton}
            onPnlAnalysisClick={() => navigate("/user_profile/wallet/options/pnl-analysis")}
          />
        </div>

      </div>

      {/* Calls / Puts recent trades below histories — hidden (set SHOW_OPTIONS_CALLS_PUTS_TRADES to re-enable) */}
      {false && (
      <div className="options-recent-trades-container">
        <div className="options-recent-trades-panel">
          <div className="options-recent-trades-header">
            Calls{callVol24h > 0 ? <span className="options-recent-trades-vol">24h Vol: {fmt(callVol24h, 2)}</span> : null}
          </div>
          <div className="options-recent-trades-body-wrap table-responsive">
            <table className="options-recent-trades-table desktop-only-table">
              <thead>
                <tr>
                  <th>Side</th>
                  <th>Symbol</th>
                  <th>Price</th>
                  <th>Size (BTC)</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {(callRecentTrades.length ? callRecentTrades : []).map((trade) => {
                  const row = mapRecentTradeRow(trade);
                  return (
                  <tr key={`call-${row.id}`}>
                    <td className={row.side === "Buy" ? "text-green" : "text-danger"}>{row.side}</td>
                    <td>{row.symbol}</td>
                    <td className={row.side === "Buy" ? "text-green" : "text-danger"}>{row.price}</td>
                    <td>{row.size}</td>
                    <td>{row.time}</td>
                  </tr>
                    );
                  })}
                {callRecentTrades.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-3">{selectedOption ? "No recent trades" : "Select a contract to view recent trades"}</td></tr>
                )}
              </tbody>
            </table>

            <div className="options-recent-trades-cards mobile-only-cards">
              {(callRecentTrades.length ? callRecentTrades : []).map((trade) => {
                const row = mapRecentTradeRow(trade);
                return (
                <div key={`call-card-${row.id}`} className="options-trade-card">
                  <div className="options-trade-card-header">
                    <span className="trade-symbol">{row.symbol}</span>
                    <span className="trade-time">{row.time}</span>
                  </div>
                  <div className="options-trade-card-body">
                    <div className="trade-col">
                      <span className="trade-label">Side</span>
                      <span className={`trade-val ${row.side === "Buy" ? "text-green" : "text-danger"}`}>{row.side}</span>
                    </div>
                    <div className="trade-col">
                      <span className="trade-label">Price</span>
                      <span className={`trade-val ${row.side === "Buy" ? "text-green" : "text-danger"}`}>{row.price}</span>
                    </div>
                    <div className="trade-col">
                      <span className="trade-label">Size ({selectedAsset})</span>
                      <span className="trade-val">{row.size}</span>
                    </div>
                  </div>
                </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="options-recent-trades-panel">
          <div className="options-recent-trades-header">
            Puts{putVol24h > 0 ? <span className="options-recent-trades-vol">24h Vol: {fmt(putVol24h, 2)}</span> : null}
          </div>
          <div className="options-recent-trades-body-wrap table-responsive">
            <table className="options-recent-trades-table desktop-only-table">
              <thead>
                <tr>
                  <th>Side</th>
                  <th>Symbol</th>
                  <th>Price</th>
                  <th>Size (BTC)</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {(putRecentTrades.length ? putRecentTrades : []).map((trade) => {
                  const row = mapRecentTradeRow(trade);
                  return (
                  <tr key={`put-${row.id}`}>
                    <td className={row.side === "Buy" ? "text-green" : "text-danger"}>{row.side}</td>
                    <td>{row.symbol}</td>
                    <td className={row.side === "Buy" ? "text-green" : "text-danger"}>{row.price}</td>
                    <td>{row.size}</td>
                    <td>{row.time}</td>
                  </tr>
                    );
                  })}
                {putRecentTrades.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-3">{selectedOption ? "No recent trades" : "Select a contract to view recent trades"}</td></tr>
                )}
              </tbody>
            </table>

            <div className="options-recent-trades-cards mobile-only-cards">
              {(putRecentTrades.length ? putRecentTrades : []).map((trade) => {
                const row = mapRecentTradeRow(trade);
                return (
                <div key={`put-card-${row.id}`} className="options-trade-card">
                  <div className="options-trade-card-header">
                    <span className="trade-symbol">{row.symbol}</span>
                    <span className="trade-time">{row.time}</span>
                  </div>
                  <div className="options-trade-card-body">
                    <div className="trade-col">
                      <span className="trade-label">Side</span>
                      <span className={`trade-val ${row.side === "Buy" ? "text-green" : "text-danger"}`}>{row.side}</span>
                    </div>
                    <div className="trade-col">
                      <span className="trade-label">Price</span>
                      <span className={`trade-val ${row.side === "Buy" ? "text-green" : "text-danger"}`}>{row.price}</span>
                    </div>
                    <div className="trade-col">
                      <span className="trade-label">Size ({selectedAsset})</span>
                      <span className="trade-val">{row.size}</span>
                    </div>
                  </div>
                </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Funds Transfer Modal (Copied from FuturesTransferModal) */}
      {showTransferModal && (
        <div className="agcx-ft-modal-root" role="dialog" aria-modal="true" style={{ zIndex: 10001 }}>
          <button type="button" className="agcx-ft-modal-backdrop" aria-label="Close" onClick={() => setShowTransferModal(false)} />

          {!isCoinSelectOpen ? (
            /* Main Transfer Panel */
            <div className="agcx-ft-modal-panel agcx-ft-modal-panel--transfer">
              <div className="agcx-ft-modal-head">
                <h2 className="agcx-ft-modal-title">Funds Transfer</h2>
                <button type="button" className="agcx-ft-modal-close" onClick={() => setShowTransferModal(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="agcx-ft-modal-body agcx-ft-transfer-body">
                <div className="agcx-ft-transfer-banner">
                  <div className="banner-icon">
                    <img className="dark_img" src="/images/fund_account_vector.svg" alt="" />
                    <img className="light_img" src="/images/fund_account_vector_light.svg" alt="" />
                  </div>
                  <div className="banner-text">
                    Upgrade to Unified Account & Trade with Shared Spot Margin
                  </div>
                  <button type="button" className="banner-close">
                    <i className="ri-close-line"></i>
                  </button>
                </div>

                <div className="agcx-ft-transfer-row">
                  <div className="agcx-ft-transfer-field agcx-ft-transfer-field--from">
                    <label>From</label>
                    <div
                      className={`agcx-ft-transfer-box is-select ${isToDropdownOpen ? "is-active" : ""}`}
                      onClick={() => setIsToDropdownOpen(!isToDropdownOpen)}
                    >
                      Isolated Margin
                      <i className={isToDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                    </div>
                  </div>
                  <button type="button" className="agcx-ft-transfer-swap" onClick={() => {
                    const temp = fromWalletType;
                    setFromWalletType(toWalletType);
                    setToWalletType(temp);
                  }}>
                    <img src="/images/account_roatate_vector.svg" alt="swap" />
                  </button>
                  <div className="agcx-ft-transfer-field agcx-ft-transfer-field--to">
                    <label>To</label>
                    <div className="agcx-ft-transfer-box">
                      Spot
                    </div>
                  </div>
                </div>

                <div className="agcx-ft-transfer-field">
                  <label>Coin</label>
                  <div
                    className="agcx-ft-transfer-box is-coin is-select"
                    onClick={() => setIsCoinSelectOpen(true)}
                  >
                    <div className="coin-info">
                      <img src="/images/usdt_icon.png" alt="" onError={(e) => e.target.src = "/images/bitcoin_icon.png"} />
                      <span>USDT</span>
                    </div>
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                  <div className="agcx-ft-transfer-quick-coins">
                    {["0G", "USDT"].map((code) => {
                      const active = code === "USDT";
                      return (
                        <button
                          key={code}
                          type="button"
                          className={`agcx-ft-quick-coin${active ? " is-active" : ""}`}
                        >
                          <span className={`agcx-ft-quick-coin-radio${active ? " is-checked" : ""}`} />
                          {code}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="agcx-ft-transfer-field">
                  <label>Size</label>
                  <div className="agcx-ft-transfer-input-wrap">
                    <input
                      type="text"
                      placeholder="Please enter the amount"
                      value={transferAmount}
                      onChange={(e) => setTransferAmount(e.target.value)}
                    />
                    <div className="input-suffix">
                      <span>USDT</span>
                      <div className="divider"></div>
                      <button type="button" className="all-btn">All</button>
                    </div>
                  </div>
                  <div className="agcx-ft-transfer-avail">
                    Transferrable: 0.00 USDT
                  </div>
                </div>

                <div className="agcx-ft-transfer-actions">
                  <button type="button" className="agcx-ft-btn agcx-ft-btn--ghost" onClick={() => setShowTransferModal(false)}>Cancel</button>
                  <button type="button" className="agcx-ft-btn agcx-ft-btn--primary">Save</button>
                </div>
              </div>
            </div>
          ) : (
            /* Select Crypto Panel */
            <div className="agcx-ft-modal-panel agcx-ft-modal-panel--coin-select">
              <div className="agcx-ft-modal-head">
                <h2 className="agcx-ft-modal-title">Select Crypto</h2>
                <button type="button" className="agcx-ft-modal-close" onClick={() => setIsCoinSelectOpen(false)}>
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="agcx-ft-modal-body">
                <div className="agcx-ft-coin-search">
                  <input
                    type="text"
                    placeholder="Search coin"
                  />
                  <i className="ri-search-line"></i>
                </div>
                <div className="agcx-ft-coin-list">
                  <div className="agcx-ft-coin-item" onClick={() => setIsCoinSelectOpen(false)}>
                    <div className="coin-item-left">
                      <img src="/images/usdt_icon.png" alt="" />
                      <div className="coin-item-info">
                        <span className="coin-short">USDT</span>
                        <span className="coin-full">Tether</span>
                      </div>
                    </div>
                    <i className="ri-check-line text-primary"></i>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Switch Account Mode Modal */}
      {showAccountModeModal && (
        <>
          <div className="agcx-accmode-backdrop" onClick={() => setShowAccountModeModal(false)}></div>
          <div className="agcx-accmode-modal">
            <div className="agcx-accmode-header">
              <div className="agcx-accmode-title">
                <i className="ri-arrow-left-s-line" onClick={() => setShowAccountModeModal(false)}></i>
                <span>Switch Account Mode</span>
                <i className="ri-question-fill agcx-accmode-help"></i>
              </div>
              <i className="ri-file-list-3-fill agcx-accmode-history"></i>
            </div>

            <div className="agcx-accmode-body">
              {/* Classic Spot Mode */}
              <div className={`agcx-accmode-card ${activeAccountMode === "classic" ? "active" : ""}`} onClick={() => setActiveAccountMode("classic")}>
                <div className="agcx-accmode-card-header">
                  <div className="agcx-accmode-card-title">
                    Classic Spot Mode <span className="badge-gray">Same as before</span>
                  </div>
                  {activeAccountMode === "classic" && <i className="ri-checkbox-circle-fill active-icon"></i>}
                </div>
                <div className="agcx-accmode-card-desc">Regular traders</div>
                <div className="agcx-accmode-tags">
                  <span>Spot</span>
                </div>

                {showClassicMore && (
                  <div className="agcx-accmode-more-content" onClick={(e) => e.stopPropagation()}>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Risk Control</div>
                      <ul className="more-sec-list">
                        <li>It remains the same as before the upgrade. With isolated trading types and risks, one account's loss won't have a ripple effect on others.</li>
                        <li>The unrealized PnL across different trading types cannot be offset.</li>
                      </ul>
                    </div>
                  </div>
                )}

                <div className="agcx-accmode-showmore" onClick={(e) => { e.stopPropagation(); setShowClassicMore(!showClassicMore); }}>
                  {showClassicMore ? "Show Less" : "Show More"} <i className={showClassicMore ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </div>
              </div>

              {/* Single-Currency Margin Mode */}
              <div className={`agcx-accmode-card ${activeAccountMode === "single" ? "active" : ""}`} onClick={() => setActiveAccountMode("single")}>
                <div className="agcx-accmode-card-header">
                  <div className="agcx-accmode-card-title">Single-Currency Margin Mode</div>
                  {activeAccountMode === "single" && <i className="ri-checkbox-circle-fill active-icon"></i>}
                </div>
                <div className="agcx-accmode-card-desc">Regular traders</div>
                <div className="agcx-accmode-tags">
                  <span>Spot</span><span>USDT Perp</span><span>Options</span>
                </div>

                {showSingleMore && (
                  <div className="agcx-accmode-more-content" onClick={(e) => e.stopPropagation()}>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Risk Control</div>
                      <ul className="more-sec-list">
                        <li>Isolate USDT from other assets and only USDT assets will be affected in liquidation.</li>
                      </ul>
                    </div>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Margin</div>
                      <ul className="more-sec-list">
                        <li>USDT assets serve as margin for Futures and Options.</li>
                        <li>Futures unrealized PnL and the USDT flexible subscriptions in Simple Earn can be used to open positions.</li>
                        <li>The cross and isolated modes are supported for USDT Perp.</li>
                      </ul>
                    </div>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">More</div>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a href="#" className="more-sec-link">About Single-Currency Margin Mode</a>
                    </div>
                  </div>
                )}

                <div className="agcx-accmode-showmore" onClick={(e) => { e.stopPropagation(); setShowSingleMore(!showSingleMore); }}>
                  {showSingleMore ? "Show Less" : "Show More"} <i className={showSingleMore ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </div>
              </div>

              {/* Multi-Currency Margin Mode */}
              <div className={`agcx-accmode-card ${activeAccountMode === "multi" ? "active" : ""}`} onClick={() => setActiveAccountMode("multi")}>
                <div className="agcx-accmode-card-header">
                  <div className="agcx-accmode-card-title">Multi-Currency Margin Mode</div>
                  {activeAccountMode === "multi" && <i className="ri-checkbox-circle-fill active-icon"></i>}
                </div>
                <div className="agcx-accmode-card-desc">Professional traders</div>
                <div className="agcx-accmode-tags">
                  <span>Spot</span><span>Cross Margin</span><span>USDT Perp</span><span>Options</span>
                </div>

                {showMultiMore && (
                  <div className="agcx-accmode-more-content" onClick={(e) => e.stopPropagation()}>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Risk Control</div>
                      <ul className="more-sec-list">
                        <li>PnL across different trading types can offset each other, but liquidation could wipe out your entire margin.</li>
                      </ul>
                    </div>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Margin</div>
                      <ul className="more-sec-list">
                        <li>USD value of multiple assets serves as margin for Cross Margin, Futures, and Options.</li>
                        <li>Futures unrealized PnL and Simple Earn flexible subscriptions can be used to open positions.</li>
                        <li>The cross and isolated modes are supported for USDT Perp.</li>
                      </ul>
                    </div>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">More</div>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a href="#" className="more-sec-link">About Multi-Currency Margin Mode</a>
                    </div>
                  </div>
                )}

                <div className="agcx-accmode-showmore" onClick={(e) => { e.stopPropagation(); setShowMultiMore(!showMultiMore); }}>
                  {showMultiMore ? "Show Less" : "Show More"} <i className={showMultiMore ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </div>
              </div>

              {/* Portfolio Margin Mode */}
              <div className={`agcx-accmode-card ${activeAccountMode === "portfolio" ? "active" : ""}`} onClick={() => setActiveAccountMode("portfolio")}>
                <div className="agcx-accmode-card-header">
                  <div className="agcx-accmode-card-title">Portfolio Margin Mode</div>
                  {activeAccountMode === "portfolio" && <i className="ri-checkbox-circle-fill active-icon"></i>}
                </div>
                <div className="agcx-accmode-card-desc">Advanced hedge traders</div>
                <div className="agcx-accmode-tags">
                  <span>Spot</span><span>Cross Margin</span><span>USDT Perp</span><span>Options</span>
                </div>
                <div className="agcx-accmode-toggle-row" onClick={(e) => e.stopPropagation()}>
                  <span>Spot Hedging</span>
                  <label className="agcx-toggle-switch">
                    <input type="checkbox" />
                    <span className="slider round"></span>
                  </label>
                </div>

                {showPortfolioMore && (
                  <div className="agcx-accmode-more-content" onClick={(e) => e.stopPropagation()}>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Risk Control</div>
                      <ul className="more-sec-list">
                        <li>PnL across different trading types can offset each other, but liquidation could wipe out your entire margin.</li>
                      </ul>
                    </div>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">Margin</div>
                      <ul className="more-sec-list">
                        <li>USD value of multiple assets serves as margin for Cross Margin, Futures, and Options.</li>
                        <li>Futures unrealized PnL and Simple Earn flexible subscriptions can be used to open positions.</li>
                        <li>All positions of the same underlying coin are grouped as a risk unit. Margin is calculated after risk hedging within each risk unit.</li>
                        <li>Cross mode is supported for USDT Perp.</li>
                      </ul>
                    </div>
                    <div className="agcx-accmode-more-section">
                      <div className="more-sec-title">More</div>
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a href="#" className="more-sec-link">About Portfolio Margin Mode</a>
                    </div>
                  </div>
                )}

                <div className="agcx-accmode-showmore" onClick={(e) => { e.stopPropagation(); setShowPortfolioMore(!showPortfolioMore); }}>
                  {showPortfolioMore ? "Show Less" : "Show More"} <i className={showPortfolioMore ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                </div>
              </div>
            </div>

            <div className="agcx-accmode-footer">
              {activeAccountMode === "single" ? (
                <button className="agcx-accmode-footer-btn disabled">Current account mode</button>
              ) : (
                <button className="agcx-accmode-footer-btn active-btn" onClick={() => setShowAccountModeModal(false)}>Confirm Switch</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OptionsPage;
